import {
  angleModeFor, addFitbVersion, applyFitbVersion, branchSetsToCheck, removeFitbVersion, versionRollKey, writeFitbVersion, visibleTargeted, FITB_BLANK_EMPTY, rolesForMode, roleForMode, displayEvaluation,
  buildFitbWordTrigger, fitbReachability, branchesUnreachableInRange, checkBranchCoverage, enumerateFormulaValues, formulaBoundsCheap, formulaComboCount, evaluateEquation, evaluateFitb, fitbBranchMissingBlanks,
  normalizeKeyword, parseFitbWordTrigger, parseThreshold, resolveEffectiveBlanks, resolveMatchedBranch, type AngleMode, type ConditionalRoleBranch,
  type FitbBlockData, type FitbBlank, type FitbBlankWord, type FeedbackState, type ScenarioRoleBranch, activeFeedbackForBranch, pickScenarioBranch, resolveScenarioBlanks, unreviewedCount, drawnScenarioId , FREE_TEXT_ANSWER_MAX_CHARS } from '@agavi/blocks';
import { DndContext, KeyboardSensor, MouseSensor, TouchSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, FormInput, GripVertical, Minus, Trash2, X } from 'lucide-react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { useAnswerRefHtml } from './answer-ref-context';
import { getBlankIdsInOrder, renderBlanksForDisplay, renderBlanksWithAnswers } from './blank-chip-preview';
import { useArmedDelete } from './block-actions';
import { type BlockIssue, findDuplicateGroups, type IssueCache, type IssueContext, issueFieldIds, IssuesPanel, memoizedByKey, useIssueContext } from './block-issues';
import { BlockQuestionEditor } from './block-question-editor';
import { BranchConditionCarousel } from './branch-condition-carousel';
import { requestBranchJump, useBranchJumpTarget } from './branch-jump';
import { formatCoverageList, minSampleValues } from './branch-setup';
import { BranchingCorrectsRow } from './branching-corrects-row';
import { CollapsedInputBlock, collapsedModeBar, collapsedIssueBar, type TestBarChip, type TestBarProps } from './collapsed-input-block';
import { ConditionChipRow, toTestBarChips, type ConditionChips } from './condition-chips';
import { dndA11y } from './dnd-a11y';
import { chipToneClass } from './eval-chip';
import { FeedbackBranchPager } from './feedback-branch-pager';
import { FeedbackCard, ChipDivider, TRIGGER_CHIP_CLASS, TRIGGER_CHIP_TEXT_CLASS, type FeedbackCardProps } from './feedback-card';
import { FiredFeedback } from './fired-feedback';
import { ReportTestChips, useReportAnswer, usePriorAnswer } from './fired-states';
import { stripHtmlForLabel } from './formula-plaintext';
import { gradingModes } from './grading-modes';
import { EditorSectionHeading, InputBlockFrame } from './input-block-frame';
import { OptionsSection } from './options-section';
import { DrivenRowFrame, ScenarioSetupFrame } from './scenario-row-frame';
import { StrandWarning, useArmSettle } from './strand-warning';
import { TriggerDropdown } from './trigger-dropdown';
import { useBlockVersions, useTestResetOnVersionChange, useVersionSeeding, versionStructure } from './use-block-versions';
import { useDrivenRowDeleteWarning } from './use-driven-row-dependents';
import { useDrivenVersionProjection } from './use-driven-versions';
import { useInputBlockFeedback, normalizeStandardFeedback, hasEmptyFeedbackContent, positiveTriggerForMode, resetContentIfCustomized, retriggerPositiveSlot } from './use-input-block-feedback';
import { makeRowBranch, nestedBranching, type SeedMode } from './use-nested-branching';
import { useRestoredData } from './use-restored-data';
import { useScenarioFollower } from './use-scenario-follower';
import { useDelayedStickyError, useIssueSettling } from './use-settled-value';
import { useSharedEditorToolbar } from './use-shared-editor-toolbar';
import { useIsPhone, useIsPhoneLandscape } from './use-viewport';
import { ANSWER_COLUMN_CLASS, BlockQuestion, hasRollableVariables, htmlHasVariableChips, processVariableChipHTML, renderChipsForDisplay, rollVariableValues, useBlockPreview, useChipScope, variableChipNames } from './variable-chip-preview';
import { QuestionHeadingLink, VariesWithSection } from './varies-with-section';
import { VersionBranchNotice } from './version-branch-notice';
import { VersionSelector } from './version-selector';

import type { Block, TaskVariable } from '@/types/experience';
import type { Editor } from '@tiptap/core';

import { AddRowButton } from '@/components/ui/add-row-button';
import { ARMED_DELETE_BLEED, ARMED_DELETE_PAINT } from '@/components/ui/armed-delete';
import { CheckboxRow } from '@/components/ui/checkbox-row';
import { IconButton } from '@/components/ui/icon-button';
import { TextField } from '@/components/ui/text-field';
import { useBlockAngleModes } from '@/contexts/angle-mode-context';
import { useTaskVariables, useTaskPreviewRoll } from '@/contexts/task-variables-context';
import { catchAllTitle, catchAllChips, fitbAnyOther, fitbChips } from '@/lib/block-condition-chips';
import { isNameTaken } from '@/lib/block-naming';
import { fitbTargetedTitleText, fitbWordLabelText } from '@/lib/block-state-labels';
import { settingAvailable } from '@/lib/mode-gated-settings';
import { sanitizeAuthoredHtml } from '@/lib/sanitize-authored-html';

/** Bridges "jump to this branch" from the live-test widget to the editor — see Dropdown/Keyword's
 * identical `requestBranchJump`/`branchJumpListeners`/`pendingBranchJump` for the full reasoning
 * (two separate mounted instances of this component, no other channel to hand off a value like
 * this). Deliberately NOT automatic on every reroll — only fires when the teacher explicitly clicks
 * the jump action. */
// ─── Types ────────────────────────────────────────────────────────────────────

interface FitbBlockProps {
  block: Block;
  isPreview: boolean;
  /** Bumped once per click of Preview's single bottom Submit — see task-content.tsx's own doc
   * comment. Only meaningful while `isPreview`; unused otherwise. */
  previewSubmitSignal?: number;
  onUpdate: (data: Record<string, unknown>) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  onSelect?: () => void;
  isSelected?: boolean;
  isDragOver?: boolean;
  isMain?: boolean;
  embedded?: boolean;
  onClose?: () => void;
  mobileIsEditing?: boolean;
  initialIsEditingFeedback?: boolean;
  onFeedbackModeChange?: (isEditing: boolean) => void;
  blocks?: Block[];
  onRename?: (name: string) => void;
}

interface TriggerOption {
  value: string;
  label: string;
  evaluation?: 'correct' | 'incorrect' | 'neutral';
}

type GradingMode = FitbBlockData['gradingMode'];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function makeWord(role: FitbBlankWord['role'] = 'neutral'): FitbBlankWord {
  return { id: crypto.randomUUID(), text: '', role };
}

/** Human-readable label for a single word — its own text, or a positional fallback
 * ("Word 2") when blank, so two blank words never produce the same label. */
/** The sentinel identifying ONE blank's "Any Other Word" catch-all, so the legend lights up just
 *  that blank's when the word typed there matched nothing listed. Per blank, not shared: a single
 *  sentinel would light every blank's catch-all the moment any one of them fell through. See
 *  `sourceId` on `ConditionChip`. */

/**
 * FITB's two default conditions as one chip list — the derivation the feedback panel and the test
 * legend now share.
 *
 * They were two copies, and the legend's own comment claimed it "mirrors blankIncorrectRow exactly"
 * — a comment asserting an agreement that nothing enforced. They had in fact already diverged on
 * the point that matters most here: the legend wrote the CONNECTORS between blanks and the panel
 * did not, because the panel stacked one row per blank and let the layout imply the relationship.
 *
 * Those connectors are the whole grammar of this block, and the two sides differ:
 *
 *   - **Correct** is a conjunction. `evaluateFitb` fires it only when `matches.every(...)` holds, so
 *     every blank must be right: blanks join with "and", while one blank's own synonyms join with
 *     "or".
 *   - **Default Incorrect** is the complement of that conjunction, and the complement of (A and B)
 *     is (not A or not B) — so it fires as soon as ANY blank is wrong, and blanks join with "or".
 *
 * Each chip carries its own `Blank N:` prefix rather than relying on a row label, because a flat
 * list is what both surfaces can draw and what lets the connectors sit between the terms they
 * actually join.
 *
 * The per-blank "Any Other Word" catch-all stays generic and is shown IN ADDITION to that blank's
 * uncovered listed words. A blank is free text, so its complement cannot be enumerated — the listed
 * wrong words that no targeted state covers can be named, and everything else cannot.
 */
/** Moved to `lib/block-condition-chips` — see that file's note. */

/** Derived in `block-state-labels` now, so Flow reads the same function this writes with. */
const fitbWordLabel = fitbWordLabelText;

/** The mechanically-derived, non-editable title for a targeted feedback state — reuses the
 * same "Answer {{ordinal}}: {{word}}" label already shown in the trigger-picker dropdown, so
 * it always reflects exactly which blank and word it targets. Two feedbacks can only collide
 * on title when they genuinely target the same blank+word (a real setup error), never by
 * coincidence — including the case where two DIFFERENT blanks happen to accept the same word
 * text, since the blank ordinal is part of the label. */
const fitbTargetedTitle = fitbTargetedTitleText;

/** Re-derives every targeted feedback title against the current, question-ordered blanks —
 * needed whenever a word's text changes, a blank's words are reordered, or editing the question
 * text reorders/adds/removes blanks (all of which shift the ordinal or word position a title
 * embeds). `orderedBlanks` must be in question order (see visibleBlanks), not raw state order. */
function resyncFitbTargetedTitles(orderedBlanks: FitbBlank[], states: FeedbackState[], t: (key: string, opts?: Record<string, unknown>) => string): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted') return s;
    const parsed = parseFitbWordTrigger(s.trigger);
    if (!parsed) return s;
    const blankIdx = orderedBlanks.findIndex(b => b.id === parsed.blankId);
    if (blankIdx === -1) return s;
    const wordIdx = orderedBlanks[blankIdx]!.words.findIndex(w => w.id === parsed.wordId);
    if (wordIdx === -1) return s;
    return { ...s, title: fitbTargetedTitle(blankIdx, orderedBlanks[blankIdx]!.words[wordIdx]!, wordIdx, t) };
  });
}

/** Targeted feedback's evaluation always mirrors its target word's role — keeps the two from
 * silently drifting apart, same fix as Dropdown's `resyncDropdownTargetedEvaluations`.
 *
 * Includes `role === 'correct'` — this state becomes functionally unreachable the instant its
 * own word is correct (correct-first-terminal means it's never even consulted), so the actual
 * evaluation value stops mattering at runtime either way, but it must still update: Flow's
 * branch-collapsing (flow-utils.ts's feedbackStatesOf) relies on evaluation === 'correct' to
 * detect "this branch's copy is dead, exclude it from this condition's scope" — leaving it stale
 * here silently broke that detection, letting a targeted state for a now-correct word keep
 * showing up as a live Flow condition. The block's own editor was never affected by the staleness
 * (isTargetedStaleForActive already determines visibility via a direct role lookup, not this
 * field), which is exactly why this went unnoticed until Flow started trusting evaluation. */
function resyncFitbTargetedEvaluations(blanks: FitbBlank[], states: FeedbackState[]): FeedbackState[] {
  return states.map(s => {
    if (s.type !== 'targeted') return s;
    const parsed = parseFitbWordTrigger(s.trigger);
    if (!parsed) return s;
    const blank = blanks.find(b => b.id === parsed.blankId);
    const word = blank?.words.find(w => w.id === parsed.wordId);
    if (!word) return s;
    return { ...s, evaluation: word.role };
  });
}

function makeBlank(id: string): FitbBlank {
  return { id, words: [makeWord('correct')] };
}

function initData(raw: Record<string, unknown>): FitbBlockData {
  const rawBlanks = raw.blanks as FitbBlank[] | undefined;
  const feedback = (raw.feedback ?? { states: [] }) as FitbBlockData['feedback'];
  // Branch-owned feedback (see ConditionalRoleBranch in @agavi/blocks) is newer than
  // conditionalBranches itself — see Dropdown/Keyword's identical backfill for the full reasoning
  // (a branch saved before that field existed has no `.feedback` at all; reading `.feedback.states`
  // off one of those unconditionally would crash the whole render tree).
  type RawBranch = Omit<ConditionalRoleBranch, 'feedback'> & { feedback?: ConditionalRoleBranch['feedback'] };
  const rawBranches = raw.conditionalBranches as RawBranch[] | undefined;
  const conditionalBranches: ConditionalRoleBranch[] | undefined = rawBranches?.map(b => ({
    ...b,
    // Deterministic, not `crypto.randomUUID()`. `initData` runs during render, so minting here
    // gave every render a fresh set of state ids for any branch that has not authored its own
    // feedback — and a feedback state's id is what `firedIds`, Flow rules and targeted triggers
    // all match on. Same fault that made a fresh MCQ grade its own correct answer wrong.
    feedback: b.feedback ?? { states: feedback.states.map(s => ({ ...s, id: `${b.id}-${s.id}` })) },
  }));
  return {
    question: (raw.question as string) ?? '',
    blanks: rawBlanks ?? [],
    caseSensitive: (raw.caseSensitive as boolean) ?? false,
    accentSensitive: (raw.accentSensitive as boolean) ?? true,
    gradingMode: (['graded', 'survey', 'optional'].includes(raw.gradingMode as string)
      ? raw.gradingMode as GradingMode
      : 'graded'),
    feedback,
    conditionalEquation: raw.conditionalEquation as FitbBlockData['conditionalEquation'],
    conditionalBranches,
    conditionalBranchesDraft: raw.conditionalBranchesDraft as FitbBlockData['conditionalBranchesDraft'],
    angleMode: (['degrees', 'radians'].includes(raw.angleMode as string) ? raw.angleMode as AngleMode : 'degrees'),
    angleModes: (raw.angleModes as Record<string, AngleMode> | undefined),
    /* Builds a NEW object field by field, so anything not named is dropped — which is how a bank
       comes to be created, written to Mongo and then silently gone. */
    /* Same hazard: a scenario link read back as absent is a block that silently stops
       following, with its per-scenario answers orphaned behind it. */
    followsBlockId: raw.followsBlockId as FitbBlockData['followsBlockId'],
    /* And the driven link, by the same rule and for the same reason. */
    drivenBy: raw.drivenBy as FitbBlockData['drivenBy'],
    scenarioBranches: raw.scenarioBranches as FitbBlockData['scenarioBranches'],
    versions: raw.versions as FitbBlockData['versions'],
  };
}

/** Backfills the default feedback set for a block read straight from `block.data` — the editor
 * seeds these into its own state, but nothing reaches the saved block until the teacher changes
 * something, so without this a freshly added block's test legend has no states to show while the
 * feedback panel beside it lists three. Mirrors Dropdown/Keyword/MCQ's own normalization, shared by
 * both non-editing render paths (the collapsed card's test widget and Preview) so they cannot drift
 * into two different sets of defaults. */
function buildNormalizedFitbData(
  rawD: FitbBlockData,
  t: (key: string, opts?: Record<string, unknown>) => string,
  /** The version drawn for this student, or the one the teacher's pager is on. */
  versionId?: string,
): FitbBlockData {
  const rawVersioned = applyFitbVersion(rawD, versionId);
  return {
    ...rawVersioned,
    feedback: normalizeStandardFeedback(rawD.feedback, t, rawD.gradingMode, {
      extraDefaults: [{
        trigger: 'incomplete_answer',
        title: t('blocks.feedbackDefaults.incompleteAnswerTitle'),
        content: `<p>${t('blocks.feedbackDefaults.incompleteAnswerContent')}</p>`,
        evaluation: 'neutral',
      }],
    }),
  };
}

/**
 * THIS BLOCK'S ISSUES FROM ITS RAW STORED DATA — the entry point the dispatcher calls.
 *
 * `fitbIssues` takes NORMALISED data, and every block normalises differently: some apply a version,
 * some need `t` for a default label, one needs a placeholder string. Rather than teach the
 * dispatcher sixteen recipes, each block keeps the knowledge of how to read itself and exposes
 * one function of a `Block`.
 *
 * NO VERSION POINTER: the board has no "shown version", so this resolves the base and the checks
 * that care walk the whole bank themselves, which is what they already do for the editor.
 */
export function fitbBlockIssues(block: Block, ctx: IssueContext): BlockIssue[] {
  return fitbIssues(buildNormalizedFitbData(initData(block.data), ctx.t), ctx);
}

export function fitbIssues(d: FitbBlockData, ctx: IssueContext): BlockIssue[] {
  const { t, variables } = ctx;
  /* The editor walks its own unsaved question html; everyone else gets the same answer from `d`. */
  const idsInOrder = ctx.blankIdsInOrder ?? getBlankIdsInOrder(d.question);
  const issues: BlockIssue[] = [];
  const categoryQuestion = t('blocks.areas.question');
  const categoryAnswerSetup = t('blocks.areas.answerSetup');
  /* UNREVIEWED ROWS, counted — the strip inside the box only speaks for the row you are on, so
     a teacher who never pages to scenario 5 never learns it was never decided. */
  const unreviewedRows = unreviewedCount(d.scenarioBranches);
  if (unreviewedRows > 0) {
    issues.push({
      key: 'scenarios-unreviewed', severity: 'warning', kind: 'incompletion',
      category: categoryAnswerSetup,
      detail: t('blocks.scenario.unreviewedScenarios', { count: unreviewedRows }),
    });
  }
  const categoryAlternativeCorrectStates = t('blocks.areas.alternativeAnswerSetup');
  const categoryFeedback = t('blocks.areas.feedback');
  const visibleBlanksForIssues: FitbBlank[] = idsInOrder.map(id => d.blanks.find(b => b.id === id) ?? makeBlank(id));
  const wordIsBlank = (w: FitbBlankWord) => !w.text.trim();
  // This is "nothing has been typed anywhere yet" (the same severity/urgency as MCQ's "All options
  // are blank"/Keyword's "All keywords are blank"), distinct from "words exist but none are marked
  // correct." Takes priority over the no-correct-answer checks below, same as those two blocks'
  // own allOptionsBlank checks.
  const allWordsBlank = visibleBlanksForIssues.length > 0 && visibleBlanksForIssues.every(blank => blank.words.length === 0 || blank.words.every(wordIsBlank));

  if (!d.question) issues.push({ key: 'question-blank', severity: 'error', kind: 'incompletion', shownInPlace: true, category: categoryQuestion, detail: t('blocks.issues.questionBlank') });
  // A passage with no blanks in it is prose, not a question — there is nothing for a student to
  // answer. Every other answer-options check here is scoped to the blanks that exist
  // (`visibleBlanksForIssues.forEach`, and allWordsBlank's own `length > 0` guard), so the case of
  // having NONE fell through all of them and reported nothing at all. Not `shownInPlace`: the card
  // renders the passage either way, and a passage missing its blanks reads as ordinary prose
  // rather than as a visible gap.
  if (visibleBlanksForIssues.length === 0) {
    issues.push({ key: 'no-blanks', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.fitb.noBlanksWarning') });
  }
  if (allWordsBlank) {
    issues.push({ key: 'words-blank', severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.fitb.allWordsBlankWarning') });
  } else if (d.gradingMode === 'graded') {
    if (d.conditionalBranches && d.conditionalBranches.length > 0) {
      // A word marked correct in this branch but left with blank text is a dead entry — the same
      // "blank text can't count as configured" rule fitbReachability applies to the answer key,
      // generalized per-blank since FITB allows multiple correct words per blank
      // (unlike MCQ/Dropdown's single find()-first-correct assumption).
      d.conditionalBranches.forEach((b, i) => {
        visibleBlanksForIssues.forEach((blank, blankIdx) => {
          const branchCorrectWord = blank.words.find(w => b.roles[w.id] === 'correct');
          if (branchCorrectWord && wordIsBlank(branchCorrectWord)) {
            issues.push({ key: `branch-${b.id}-correct-blank-${blank.id}`, severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.fitb.diagnosticBranchCorrectBlank', { number: i + 1, ordinal: blankIdx + 1 }) });
          }
        });
      });
    } else {
      // Same two-way split as Keyword's static check: a blank with NO word marked correct is a
      // different problem ("missing") from a blank whose only correct-marked word(s) are all
      // left with blank text ("blank") — name each blank individually rather than one blanket
      // "No Correct Answers"/"Missing Correct Answers", so a teacher with several blanks doesn't
      // have to hunt for which one needs fixing.
      visibleBlanksForIssues.forEach((blank, blankIdx) => {
        const correctWords = blank.words.filter(w => w.role === 'correct');
        if (correctWords.length === 0) {
          issues.push({ key: `missing-correct-blank-${blank.id}`, fields: [blank.id], severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.fitb.missingCorrectAnswerForBlank', { ordinal: blankIdx + 1 }) });
        } else if (correctWords.every(wordIsBlank)) {
          issues.push({ key: `correct-blank-${blank.id}`, fields: [blank.id], severity: 'error', kind: 'incompletion', category: categoryAnswerSetup, detail: t('blocks.fitb.correctWordBlankForBlank', { ordinal: blankIdx + 1 }) });
        }
      });
    }
  }

  // Duplicate words within the SAME blank are a real evaluation bug, not just clutter — matching
  // finds the FIRST word whose NORMALIZED text matches, so a second word that normalizes to the
  // same comparison key (even a different role) is silently unreachable. Keyed on normalizeKeyword
  // using this block's OWN caseSensitive/accentSensitive settings — not a fixed exact-string rule
  // — since whether "cafe" and "Café" actually collide at evaluation time depends entirely on
  // those toggles. A byte-identical pair always collides regardless of settings, so this is a
  // strict superset of a plain exact-string check. Scoped per blank, not across the whole question
  // — the same word accepted in two DIFFERENT blanks is normal and harmless, since each blank is
  // matched independently.
  visibleBlanksForIssues.forEach((blank, blankIdx) => {
    for (const group of findDuplicateGroups(blank.words, w => normalizeKeyword(w.text, d.caseSensitive, d.accentSensitive))) {
      const uniqueTexts = [...new Set(group.map(w => w.text.trim()))];
      issues.push({
        key: `duplicate-${blank.id}-${group.map(w => w.id).join('-')}`,
        fields: group.map(w => w.id),
        severity: 'error',
        kind: 'defect',
        category: categoryAnswerSetup,
        detail: uniqueTexts.length > 1
          ? t('blocks.fitb.duplicateWordNormalizedWarning', { ordinal: blankIdx + 1, texts: uniqueTexts.map(x => `"${x}"`).join(', ') })
          : t('blocks.fitb.duplicateWordWarning', { ordinal: blankIdx + 1, word: uniqueTexts[0] }),
      });
    }
  });

  // Everything below requires an explicit action (turning on branching, typing an equation,
  // marking a role) — see keywordIssues' own comment for why none of this needs the grace
  // period, and why it's NOT gated on setupState either.
  /* PER VERSION: these checks read version-resolved data, so a banked block was only ever
     validated for the version being displayed. See `branchSetsToCheck`. */
  if (d.gradingMode === 'graded') {
    for (const set of branchSetsToCheck<ConditionalRoleBranch>(d)) {
      const branches = set.branches ?? [];
      if (branches.length === 0) continue;
      /** Keys must differ per set, or two sets' gaps collapse into one issue. */
      const vkey = (k: string) => (set.versionIndex !== null ? `v${set.versionIndex}-${k}`
        : set.scenarioIndex !== null ? `s${set.scenarioIndex}-${k}` : k);
      /** And the message must say WHICH one, or it names a condition nobody can find. */
      const vlabel = (detail: string) => (set.versionIndex !== null
        ? t('blocks.branching.versionScopedIssue', { number: set.versionIndex, detail })
        : set.scenarioIndex !== null
          ? t('blocks.branching.scenarioScopedIssue', { number: set.scenarioIndex, detail })
          : detail);
      const equationTrimmed = set.equation?.trim();
      if (!equationTrimmed) {
        issues.push({ key: vkey('no-equation'), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticNoEquation')) });
      } else if (evaluateEquation(equationTrimmed, minSampleValues(variables), angleModeFor(d, 'conditionalEquation')) === null) {
        issues.push({ key: vkey('invalid-equation'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.equationInvalidWarning')) });
      } else {
        // The domain narrows coverage to the values the equation can actually produce when it is a
        // bare scenario-selector variable; null everywhere else, which keeps the continuous check.
        const domainValues = enumerateFormulaValues(equationTrimmed, variables);
        // Too many values to build the set, but still a discrete variable. The real-line gap
        // analysis would then name regions it can never produce ("<0, >0" for a 1-to-100
        // variable), so gaps are suppressed and the teacher is told coverage went unchecked.
        const comboCount = domainValues ? null : formulaComboCount(equationTrimmed, variables);
        const discreteButUnchecked = comboCount !== null;
        const coverage = checkBranchCoverage(branches, domainValues);
        const gapIssues = coverage.issues.filter(i => i.matchingBranchIds.length === 0);
        const overlapIssues = coverage.issues.filter(i => i.matchingBranchIds.length > 0);
        if (gapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-gap'), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.missingCoverage', { list: formatCoverageList(gapIssues, t, 'gap') })) });
        }
        if (overlapIssues.length > 0 && !discreteButUnchecked) {
          issues.push({ key: vkey('coverage-overlap'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.overlappingConditions', { list: formatCoverageList(overlapIssues, t, 'overlap') })) });
        }
        if (discreteButUnchecked) {
          issues.push({ key: vkey('coverage-unchecked'), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
            detail: vlabel(t('blocks.branching.coverageUnchecked', { count: comboCount })) });
        }
        // Reachability survives a domain too large to enumerate: bounds alone prove a threshold
        // outside them can never be met, and for a bare variable the bounds are its own min and max.
        const cheapBounds = domainValues ? null : formulaBoundsCheap(equationTrimmed, variables, angleModeFor(d, 'conditionalEquation'));
        if (cheapBounds) {
          branchesUnreachableInRange(branches, cheapBounds.lo, cheapBounds.hi).forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: cheapBounds.lo, hi: cheapBounds.hi })) });
          });
        }
        // A condition no attainable value can satisfy: its whole answer setup is dead. A WARNING, not
        // an error — nothing a student does can reach it, so it cannot mis-grade anyone, and where it
        // does cause harm the coverage gap beside it already says so at error severity. Matches
        // `feedback-overlaps-correct`, the existing "authored but can never fire" issue.
        if (domainValues && domainValues.length > 0) {
          coverage.unreachableBranchIds.forEach(id => {
            const number = (branches).findIndex(b2 => b2.id === id) + 1;
            if (number === 0) return;
            issues.push({ key: vkey(`branch-${id}-unreachable`), severity: 'warning', kind: 'defect', category: categoryAlternativeCorrectStates,
              detail: vlabel(t('blocks.branching.branchUnreachable', { number, lo: domainValues[0], hi: domainValues[domainValues.length - 1] })) });
          });
        }
      }
      branches.forEach((b, i) => {
        // FITB's correctness is AND-across-blanks (unlike Keyword's single answer field), so a
        // branch's completeness is a per-blank question — name each affected blank ordinal rather
        // than surfacing one all-or-nothing boolean.
        const missingBlankIds = fitbBranchMissingBlanks(b, visibleBlanksForIssues);
        if (missingBlankIds.length > 0) {
          const ordinals = missingBlankIds.map(id => visibleBlanksForIssues.findIndex(bl => bl.id === id) + 1).join(', ');
          issues.push({ key: vkey(`branch-${b.id}-none`), severity: 'error', kind: 'incompletion', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.fitb.diagnosticBranchMissingBlanks', { number: i + 1, blanks: ordinals })) });
        }
        if (b.operator === 'between') {
          const lo = parseThreshold(b.threshold);
          const hi = parseThreshold(b.thresholdHigh);
          if (Number.isFinite(lo) && Number.isFinite(hi) && lo > hi) {
            issues.push({ key: vkey(`branch-${b.id}-inverted`), severity: 'error', kind: 'defect', category: categoryAlternativeCorrectStates, detail: vlabel(t('blocks.branching.diagnosticBranchInverted', { number: i + 1 })) });
          }
        }
      });
    }
  }

  // Validation asks the SAME question grading does. A targeted state in the top-level set can never
  // fire once branches are configured — `activeFeedbackForBranch` strips them in the unresolved
  // fallback — so a blank one is unreachable by construction and reporting it sent a teacher
  // looking for something the editor cannot even show them.
  const hasMissingFeedbackText = hasEmptyFeedbackContent(activeFeedbackForBranch(undefined, d.feedback, (d.conditionalBranches?.length ?? 0) > 0).states) ||
    !!d.conditionalBranches?.some(b => hasEmptyFeedbackContent(b.feedback.states));
  if (hasMissingFeedbackText) issues.push({ key: 'missing-feedback-text', severity: 'warning', kind: 'incompletion', category: categoryFeedback, detail: t('blocks.feedbackPanel.missingText') });

  return issues;
}

// ─── Question preview ──────────────────────────────────────────────────────────
// Collapsed card (revealAnswers=true) shows each blank's own correct word(s) — the teacher's own
// reference, mirrors Keyword's collapsed card showing the actual accepted keywords. isPreview
// (revealAnswers=false) simulates what a student encounters, so it stays a generic "Answer N"
// placeholder — showing the answer there would defeat the point of a preview.

function FitbQuestionPreview({ html, blanks, revealAnswers, className }: { html: string; blanks: FitbBlank[]; revealAnswers: boolean; className?: string }) {
  const preview = useBlockPreview();
  const { t } = useTranslation();
  // Answer references first, for the reason `PreviewHtml` gives: this surface renders stored
  // html directly, so without the hook a chip here would show whatever name was baked into it
  // rather than the alias's current one.
  // `preview` for the same reason `PreviewHtml` passes it: null is the authoring canvas, where
  // a reference stays a chip, and a roll is a test surface, where it resolves to a value.
  const withAnswerRefs = useAnswerRefHtml(preview)(html);
  const chipScope = useChipScope();
  const withVariables = preview !== null
    ? processVariableChipHTML(withAnswerRefs, preview, chipScope)
    : renderChipsForDisplay(withAnswerRefs, chipScope);
  const rendered = revealAnswers ? renderBlanksWithAnswers(withVariables, blanks, t) : renderBlanksForDisplay(withVariables, t);
  return <div data-authored='' className={className} dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(rendered) }} />;
}

// ─── Interactive question (student-facing inline answer inputs) ──────────────
// The first inline-interactive rich-text surface in this app. Every other block's "chip in rich
// text" content (this block's own blank-chip pill, variable chips, math/chem formulas) is either a
// read-only tiptap NodeView (while editing) or plain string substitution before
// `dangerouslySetInnerHTML` (when rendering) — nothing mounts a live controlled <input> inline
// inside externally-set HTML. This combines two patterns that already exist separately elsewhere
// in this codebase — `createPortal` (already used for `input-block-frame.tsx`'s phone full-screen
// overlay) and a ref + `querySelectorAll('[data-blank-id]')` walk (already used by
// `blank-chip-preview.tsx`, just against a detached div there) — together, for the first time, so a
// blank survives arbitrary rich-text nesting (bold/italic wrapping it) without needing to
// reconstruct the whole DOM tree as JSX.
//
// Deliberately bypasses `renderBlanksForDisplay`/`renderBlanksWithAnswers`: both REPLACE the
// blank-chip span outright (swapping `data-blank-id` for `data-blank-id-display`), which would
// remove the exact attribute this component's query selector depends on. Only variable-chip
// resolution runs on `html` before it reaches this component; the raw blank-chip span (and its
// ugly id-as-text content, per `BlankChip`'s own tiptap `renderHTML`) is left in place, then its
// text is cleared and a portal takes over as its child.
function FitbInteractiveQuestion({
  html, values, onChange, disabled, activeEditorRef, setActiveEditor,
}: {
  html: string;
  values: Record<string, string>;
  onChange: (blankId: string, text: string) => void;
  disabled?: boolean;
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
}) {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [anchors, setAnchors] = useState<{ id: string; node: HTMLElement }[]>([]);
  const idsInOrder = getBlankIdsInOrder(html);

  // Re-walks whenever the resolved HTML string changes (a question edit or a variable reroll) —
  // React skips touching dangerouslySetInnerHTML's DOM when the string is unchanged (JS strings
  // compare by value), so typing into one blank's own portaled input never triggers this, and never
  // unmounts any of the others. useLayoutEffect (not useEffect) so the portal mounts in the same
  // paint as the DOM update — no one-frame flash of the raw blank-id text.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) { setAnchors([]); return; }
    const found: { id: string; node: HTMLElement }[] = [];
    container.querySelectorAll<HTMLElement>('[data-blank-id]').forEach(span => {
      const id = span.getAttribute('data-blank-id') ?? '';
      if (!id) return;
      span.textContent = '';
      found.push({ id, node: span });
    });
    setAnchors(found);
  }, [html]);

  return (
    <>
      <div ref={containerRef} className='ws-rich-text' dangerouslySetInnerHTML={{ __html: sanitizeAuthoredHtml(html) }} />
      {anchors.map(({ id, node }) => createPortal(
        <TextField
          key={id}
          inline
          // `prose`, not `literal`, and the split is by JOB rather than by content. Mono exists so
          // an author can see whitespace and lookalikes in an ANSWER KEY; this is the student's
          // own box, sitting inside a sentence, where mono only looks wrong. The authored word in
          // SortableWordRow stays `literal`.
          variant='prose'
          value={values[id] ?? ''}
          onChange={v => onChange(id, v)}
          disabled={disabled}
          // `blankAria`, not the `blankChipLabel` the editor's visible chips use. Two reasons the
          // name says "of N" where the chip does not: a chip sits in a sentence a teacher can see
          // whole, and "Blank 1, text box, empty" put the reader's word for empty right after our
          // own — "Blank 1 ... blank". The count also tells a student how many are left.
          ariaLabel={t('blocks.fitb.blankAria', { ordinal: idsInOrder.indexOf(id) + 1, total: idsInOrder.length })}
          // Capped like Keyword's, and for the same reason — see FREE_TEXT_ANSWER_MAX_CHARS.
          maxLength={FREE_TEXT_ANSWER_MAX_CHARS}
          onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
          className='mx-0.5' />,
        node,
      ))}
    </>
  );
}

// ─── Live test data ───────────────────────────────────────────────────────────
// One shared derivation, called from 3 places (desktop collapsed card, phone Test tab, Preview) —
// same "canvas card becomes a live interactive test surface" pattern as Dropdown/MCQ/Keyword,
// evaluated fresh every render.

interface FitbTestData {
  resolvedVariables: Record<string, number>;
  matchedBranch: ConditionalRoleBranch | null;
  matchedBranchIndex: number;
  matchedEquationValue: number | null;
  resolvedQuestion: string;
  testResult: FeedbackState[] | null;
  testChips: TestBarChip[];
  hasChips: boolean;
  hasRollable: boolean;
}

function computeFitbTestData(
  d: FitbBlockData,
  idsInOrder: string[],
  variables: TaskVariable[],
  t: (key: string, opts?: Record<string, unknown>) => string,
  rolledValues: Record<string, string>,
  submittedValues: Record<string, string>,
  hasSubmitted: boolean,
  /** The leader's variant in force — see `evaluateDropdown`'s own note. */
  scenarioId?: string,
): FitbTestData {
  const resolvedVariables: Record<string, number> = Object.fromEntries(
    Object.entries(rolledValues)
      .map(([name, val]) => [name, Number(val)] as const)
      .filter((entry): entry is [string, number] => !isNaN(entry[1]))
  );
  const rawVisibleBlanks: FitbBlank[] = idsInOrder.map(id => d.blanks.find(b => b.id === id) ?? makeBlank(id));
  // Branches are only ever consulted in graded mode — see evaluateFitb's own gate. A FOLLOWER
  // resolves against the scenario in force instead, the same split the evaluator makes.
  const followed = d.gradingMode === 'graded' && !!d.scenarioBranches && d.scenarioBranches.length > 0;
  const scenarioRow = followed ? pickScenarioBranch(d.scenarioBranches!, scenarioId) : null;
  const visibleBlanks = (d.gradingMode !== 'graded'
    ? rawVisibleBlanks
    : followed
      ? resolveScenarioBlanks(rawVisibleBlanks, d.scenarioBranches, scenarioId)
      : resolveEffectiveBlanks(rawVisibleBlanks, d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
  ).map(b => ({ ...b, words: rolesForMode(d.gradingMode, b.words) }));
  const matchedBranch = !followed && d.gradingMode === 'graded'
    ? resolveMatchedBranch(d.conditionalEquation, d.conditionalBranches, resolvedVariables, angleModeFor(d, 'conditionalEquation'))
    : null;
  const matchedBranchIndex = matchedBranch ? (d.conditionalBranches ?? []).findIndex(b => b.id === matchedBranch.id) : -1;
  const matchedEquationValue = matchedBranch && d.conditionalEquation ? evaluateEquation(d.conditionalEquation, resolvedVariables, angleModeFor(d, 'conditionalEquation')) : null;
  const activeFeedbackForDisplay = scenarioRow ? scenarioRow.feedback : matchedBranch ? matchedBranch.feedback : d.feedback;
  /* NO SCOPE MARK — see `variable-chip-preview`'s exempt list. This is the TEST surface,
     where every variable has a value, so a chip here means an undefined variable, which the
     issue panel already reports. The authoring canvas goes through `FitbQuestionPreview`. */
  const resolvedQuestion = processVariableChipHTML(d.question, rolledValues);
  const testResult = hasSubmitted ? evaluateFitb({ values: submittedValues }, d, resolvedVariables, scenarioId) : null;
  const firedIds = new Set((testResult ?? []).map(s => s.id));

  // Per-blank matching — the exact same derivation evaluateFitb itself computes, reused so each
  // chip's activeness is judged against ITS OWN blank's submitted value, never a single shared
  // "the" answer like Keyword has (FITB's own added complication — chips affiliate by blank).
  const matches = visibleBlanks.map(blank => {
    const normalizedAnswer = normalizeKeyword(submittedValues[blank.id] ?? '', d.caseSensitive, d.accentSensitive);
    const word = blank.words.find(w => normalizeKeyword(w.text, d.caseSensitive, d.accentSensitive) === normalizedAnswer);
    return { blank, word };
  });

  const testChips: TestBarChip[] = [];
  // The same derivation the feedback panel reads, over the same branch-resolved blanks.
  const barReach = fitbReachability(d, visibleBlanks);
  const barCanFire = (trigger: string) => barReach.states.find(x => x.trigger === trigger)?.reachable ?? false;

  if (d.gradingMode !== 'graded') {
    /**
     * Participation: targeted first, then the catch-all naming what it still catches — the same
     * order and the same derivation the feedback panel uses. Both arms used to emit the catch-all
     * alone, so a word singled out for its own feedback never appeared on the bar at all.
     */
    if (d.gradingMode === 'survey') {
      const unanswered = activeFeedbackForDisplay.states.find(x => x.trigger === 'unanswered');
      if (unanswered) testChips.push({ id: unanswered.id, condition: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation: unanswered.evaluation, active: firedIds.has(unanswered.id), category: 'unanswered' });
    }
    let anyTargeted = false;
    visibleBlanks.forEach((blank, blankIdx) => {
      // The blank's own "left empty" target, which is not one of its WORDS and so could not be
      // found by the loop below — the bar showed no targeted row at all for one.
      if (d.gradingMode === 'optional') {
        const emptyTrigger = buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY);
        const st = activeFeedbackForDisplay.states.find(x => x.type === 'targeted' && x.trigger === emptyTrigger);
        if (st) {
          anyTargeted = true;
          testChips.push({
            id: st.id,
            condition: t('blocks.fitb.triggerOptionLabel', {
              ordinal: blankIdx + 1, word: t('blocks.feedbackDefaults.noResponseTitle'),
            }),
            evaluation: 'neutral',
            active: firedIds.has(st.id),
            category: 'targeted',
          });
        }
      }
      blank.words.forEach((word, wordIdx) => {
        const trigger = buildFitbWordTrigger(blank.id, word.id);
        const st = activeFeedbackForDisplay.states.find(x => x.type === 'targeted' && x.trigger === trigger);
        if (!st) return;
        anyTargeted = true;
        testChips.push({
          id: st.id,
          condition: t('blocks.fitb.triggerOptionLabel', { ordinal: blankIdx + 1, word: fitbWordLabel(word, wordIdx, t) }),
          evaluation: 'neutral',
          active: firedIds.has(st.id) && matches[blankIdx]?.word?.id === word.id,
          category: 'targeted',
        });
      });
    });
    /*
     * OPTIONAL is ONE ROW. Everything accumulated above belongs to a mode that can fire it, and
     * optional fires none of it: the mode is inert, so Any Response is the only outcome it has.
     * Cleared rather than never-pushed, because the rungs above are shared with survey, which
     * does fire them. `legendStatesFor` makes the same call for the blocks that use it.
     */
    if (d.gradingMode === 'optional') testChips.length = 0;
    const catchAll = activeFeedbackForDisplay.states.find(
      x => x.trigger === (d.gradingMode === 'optional' ? 'any_response' : 'any_answer'));
    if (catchAll) {
      testChips.push(...toTestBarChips(
        catchAllChips(d.gradingMode, anyTargeted, t,
          () => fitbChips('incorrect', barReach.uncoveredByBlank, t, d.gradingMode, new Set(activeFeedbackForDisplay.states.filter(x => x.type === 'targeted').map(x => x.trigger))),
          ), {
          id: catchAll.id,
          category: d.gradingMode === 'optional' ? 'any_response' : 'any_answer',
          active: firedIds.has(catchAll.id),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
    }
  } else {
    const unansweredState = activeFeedbackForDisplay.states.find(x => x.trigger === 'unanswered');
    if (unansweredState) testChips.push({ id: unansweredState.id, condition: t('blocks.feedbackDefaults.noAnswerTitle'), evaluation: unansweredState.evaluation, active: firedIds.has(unansweredState.id), category: 'unanswered' });

    const incompleteState = barCanFire('incomplete_answer') ? activeFeedbackForDisplay.states.find(x => x.trigger === 'incomplete_answer') : undefined;
    if (incompleteState) testChips.push({ id: incompleteState.id, condition: t('blocks.feedbackDefaults.incompleteAnswerTitle'), evaluation: incompleteState.evaluation, active: firedIds.has(incompleteState.id), category: 'incomplete' });

    {
      // Correct fan-out — one chip per blank's own uncovered correct word(s), grouped/labeled by
      // blank, mirroring blankCorrectRow's own word list exactly so the legend can never disagree
      // with the feedback panel's own chips (the bug found+fixed in Keyword). Connected with "or"
      // between a blank's own alternative words (any one satisfies that blank alone) and "and"
      // between different blanks (every blank must simultaneously match for Correct to fire).
      const correctState = activeFeedbackForDisplay.states.find(x => x.trigger === 'correct');
      if (correctState) {
        const correctFired = firedIds.has(correctState.id);
        // One row per blank, ALWAYS — including a blank with no typeable correct word, which gets
        // an Unreachable chip in its place. The loop used to `return` past those, so a passage set
        // up for blank 1 and not blank 2 printed only blank 1's word and read as a satisfiable
        // condition. Correct is a conjunction across blanks (`matches.every(...)` in evaluateFitb),
        // so the missing term is the whole answer, and showing it in position is what mirrors
        // blankCorrectRow's own layout in the feedback panel.
        testChips.push(...toTestBarChips(fitbChips('correct', barReach.correctByBlank, t), {
          id: correctState.id,
          category: 'correct',
          active: correctFired,
          // Correct is a conjunction, so when it fires ONE word per blank is what matched — several
          // chips live at once, and the unmatched synonyms beside them still dark. That is why the
          // envelope takes a set.
          activeSourceIds: new Set(matches.map(m => m.word?.id).filter((x): x is string => !!x)),
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
        // No blanks at all: nothing to fan out over, so the state says so on its own line.
        // No blanks at all: `fitbChips` emits the Unreachable chip itself, for both surfaces.
      }

      // Targeted fan-out — one chip per active targeted feedback state, mirroring the
      // targetedSlot's own trigger-picker labeling exactly. Each targets a distinct, independently
      // fireable condition rather than a boolean combination with the others, so no connector.
      visibleBlanks.forEach((blank, blankIdx) => {
        const match = matches[blankIdx]!;
        blank.words.forEach((word, wordIdx) => {
          if (word.role === 'correct') return;
          const trigger = buildFitbWordTrigger(blank.id, word.id);
          const state = activeFeedbackForDisplay.states.find(x => x.type === 'targeted' && x.trigger === trigger);
          if (!state) return;
          testChips.push({
            id: state.id,
            condition: t('blocks.fitb.triggerOptionLabel', { ordinal: blankIdx + 1, word: fitbWordLabel(word, wordIdx, t) }),
            evaluation: state.evaluation,
            active: firedIds.has(state.id) && match.word?.id === word.id,
            category: 'targeted',
          });
        });
      });

      // Incorrect fan-out — one chip per blank's own uncovered non-correct word, PLUS one generic
      // "Any Other Word" catch-all per blank (free text is never fully coverable), mirroring
      // blankIncorrectRow exactly. Connected with "or" throughout — a blank's own alternative
      // wrong words are OR'd (mirroring blankIncorrectRow's own connector), and so are different
      // blanks (Default Incorrect is the dual of Correct: it fires as soon as ANY blank is wrong,
      // not only once every blank is).
      const incorrectState = activeFeedbackForDisplay.states.find(x => x.trigger === 'incorrect');
      if (incorrectState) {
        const incorrectFired = firedIds.has(incorrectState.id);
        // Whichever term each blank actually landed on: its matched listed word, or that blank's own
        // catch-all when the typed word matched nothing listed.
        const liveIncorrect = new Set(barReach.uncoveredByBlank.map(({ blank }, blankIdx) =>
          matches[blankIdx]?.word?.id ?? fitbAnyOther(blank.id)));
        testChips.push(...toTestBarChips(fitbChips('incorrect', barReach.uncoveredByBlank, t), {
          id: incorrectState.id,
          category: 'incorrect',
          active: incorrectFired,
          activeSourceIds: liveIncorrect,
          unreachableLabel: t('blocks.feedbackPanel.unreachable'),
          connectorWords: { and: t('blocks.connectors.and'), or: t('blocks.connectors.or') },
        }));
        // A passage with no blanks fans out over nothing, and the category used to vanish from the
        // legend while the feedback panel still showed the Default Incorrect card (inactive).
        // `fitbChips` now emits the Unreachable chip for that case itself, so both surfaces say it
        // without either one carrying its own fallback.
      }
    }
  }

  const hasChips = htmlHasVariableChips(d.question);
  const hasRollable = hasRollableVariables(variables);

  return { resolvedVariables, matchedBranch, matchedBranchIndex, matchedEquationValue, resolvedQuestion, testResult, testChips, hasChips, hasRollable };
}

// Fired-feedback display — Preview and the live test widget both show this identically once an
// answer has been submitted. Near-verbatim port of MCQ's own `renderMCQFiredFeedback`:
// `evaluateFitb` can fire multiple targeted states in parallel (one per wrong blank), the
// opposite of Keyword's correct-first-terminal single-fire shape, so `default` states
// (unanswered/incomplete_answer/correct/incorrect/any_answer/any_response) render one box each
// (never co-fire, since evaluateFitb's early-returns are mutually exclusive) while `targeted`
// states cluster into ['incorrect', 'neutral'] buckets in that fixed order — exhaustive, since
// resyncFitbTargetedEvaluations already guarantees a targeted state's evaluation always
// mirrors its word's role, and only non-correct roles ever get one.
/** This block's fired feedback — see `FiredFeedback`, which every block now shares. */
function renderFitbFiredFeedback(testResult: FeedbackState[] | null, rolledValues: Record<string, string>): React.ReactNode {
  return <FiredFeedback states={testResult} rolledValues={rolledValues} />;
}

// ─── Answer display ───────────────────────────────────────────────────────────
// The one canonical "what a student sees" layout — question (with interactive blanks), then fired
// feedback below — shared by every place that renders a student-facing view of this block
// (currently the teacher-facing Preview and the editor's own live-test widget; the eventual
// student interface should reuse this same shape once it consumes @agavi/blocks, rather than
// re-deriving the layout independently). Identical role to Dropdown/MCQ/Keyword's own
// AnswerDisplay components.
function FitbAnswerDisplay({
  question, values, onChange, disabled, activeEditorRef, setActiveEditor, children, scenario,
}: {
  /** The leader's variant, ghosted, above the question — see `AnswerDisplayFrame`'s own note.
   *  FITB's question IS its answer surface, so the stem sits above the whole thing. */
  scenario?: React.ReactNode;
  question: string;
  values: Record<string, string>;
  onChange: (blankId: string, text: string) => void;
  disabled?: boolean;
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  children?: React.ReactNode;
}) {
  return (
    <div className='flex flex-col gap-3'>
      {scenario}
      <FitbInteractiveQuestion
        html={question}
        values={values}
        onChange={onChange}
        disabled={disabled}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
      {/*
        * THE FEEDBACK GETS THE ANSWER COLUMN; the passage does not.
        *
        * Every block that routes through `AnswerDisplayFrame` caps and centres its answer and
        * leaves the question full-width. FITB hand-rolls that shape, and so had neither — the
        * passage was right by accident and the feedback under it was left-aligned against
        * seven blocks that centre it.
        *
        * Only `children`, because FITB's answer IS the question: the blanks are inline in the
        * teacher's prose, which is theirs to align per paragraph and must not be capped to a
        * control's width. What is left to centre is exactly what the frame would have centred.
        */}
      <div className={`${ANSWER_COLUMN_CLASS} flex flex-col items-stretch gap-3`}>{children}</div>
    </div>
  );
}

// ─── Word row ─────────────────────────────────────────────────────────────────

interface SortableWordRowProps {
  word: FitbBlankWord;
  wordCount: number;
  armedWordKey: string | null;
  setArmedWordKey: (key: string | null) => void;
  roleLocked: boolean;
  onRoleClick: (id: string) => void;
  onTextChange: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  armedKey: string;
  /** A word's accepted-text is a plain string, not tiptap HTML — it never registers itself AS the
   * active editor. It only needs to CLEAR whichever editor was previously active on focus, so the
   * shared toolbar goes inert while a teacher is typing a word — same one-line pattern
   * FormulaField/Keyword's SortableOptionRow already use. */
  activeEditorRef: { current: Editor | null };
  setActiveEditor: (editor: Editor | null) => void;
  /** This word collides with another in the SAME blank, under the block's own case/accent settings.
   *  Read from `issueFieldIds(editorIssues)`, so the ring and the message cannot name different
   *  rows. Per blank, because the same word in two different blanks is normal and harmless — each
   *  blank is matched independently, which is what the issue check itself already scopes for. */
  hasDuplicate?: boolean;
}

function SortableWordRow({ word, wordCount, roleLocked, armedWordKey, setArmedWordKey, onRoleClick, onTextChange, onRemove, armedKey, activeEditorRef, setActiveEditor, hasDuplicate }: SortableWordRowProps) {
  /* Warns when this row is the only thing choosing a version somewhere later —
     see `useDrivenRowDependents`. Undefined for the overwhelming majority. */
  const strandWarning = useDrivenRowDeleteWarning(word.id);
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: word.id });
  const isArmed = armedWordKey === armedKey;
  /* See `useArmSettle`: the warning below is what moves the button. */
  const armSettled = useArmSettle(isArmed);
  // Same rule as every other error marker: wait for the row to stop changing before the ring first
  // appears, then hold it until the collision is actually gone.
  const showDuplicate = useDelayedStickyError(!!hasDuplicate, word.text);

  return (
    <div
      ref={setNodeRef}
      data-field-id={word.id}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className='flex min-w-0 flex-col gap-1'
    >
      <div
        className={`flex min-w-0 items-center gap-2 ${isDragging ? 'opacity-40' : ''} ${
          isArmed ? `rounded-lg ${ARMED_DELETE_PAINT} ${ARMED_DELETE_BLEED}` : ''
        }`}
      >
        <div
          {...listeners}
          {...attributes}
          aria-label={t('blocks.fitb.dragWord')}
          className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
        >
          <GripVertical className='h-4 w-4' />
        </div>

        <button
          type='button'
          onClick={() => onRoleClick(word.id)}
          disabled={roleLocked}
          aria-label={
            word.role === 'correct'   ? t('blocks.roleCycle.correct')
              : word.role === 'incorrect' ? t('blocks.roleCycle.incorrect')
              : t('blocks.roleCycle.neutral')
          }
          className='-mx-3 flex shrink-0 items-center justify-center p-3 disabled:cursor-not-allowed disabled:opacity-40'
        >
          <span className={`pointer-events-none flex h-5 w-5 items-center justify-center rounded-sm transition-colors ${
            word.role === 'correct' ? 'border-2 border-ws-primary bg-ws-primary' :
            word.role === 'incorrect' ? 'bg-ws-muted/20' :
            'border-2 border-ws-border'
          }`}>
            {word.role === 'correct' && <Check className='h-3.5 w-3.5 text-ws-btn-on-primary' strokeWidth={3} />}
            {word.role === 'incorrect' && <X className='h-3.5 w-3.5 text-ws-muted' strokeWidth={3} />}
            {word.role === 'neutral' && <Minus className='h-3 w-3 text-ws-muted' strokeWidth={2} />}
          </span>
        </button>

        {/* The duplicate ring goes on a WRAPPER, matching keyword's and dropdown's rows: the panel
            names the problem, the field says WHICH word it is. Both members of a colliding pair light
            up, because neither is individually wrong — it is the pair that cannot both be matched
            (matching finds the first, so the second is silently unreachable). */}
        <div className={`min-w-0 flex-1 rounded ${showDuplicate ? 'ring-1 ring-ws-error-fg' : ''}`}>
          <TextField fieldId={`${word.id}.text`}
            variant='literal'
            value={word.text}
            onChange={v => onTextChange(word.id, v)}
            ariaLabel={t('blocks.fitb.wordPlaceholder')}
            placeholder={t('blocks.fitb.wordPlaceholder')}
            // The accepted word takes the same cap as the blank it is matched against.
            maxLength={FREE_TEXT_ANSWER_MAX_CHARS}
            onFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
            className='w-full' />
        </div>

        <IconButton
          type='button'
          variant='destructive'
          size='md'
          isArmed={isArmed}
          disabled={wordCount <= 1}
          /*
             SAID TWICE, ON PURPOSE: in the armed label for a reader, and in the warning
             beside the button for everyone else. It used to be the label alone, which made
             it invisible — see `StrandWarning` for why that was chosen and why it was
             wrong. `title` stays for a pointer resting on the icon itself. */
          aria-label={isArmed
            ? [t('blocks.fitb.confirmRemoveWord'), strandWarning].filter(Boolean).join('. ')
            : t('blocks.fitb.removeWord')}
          title={isArmed ? strandWarning : undefined}
          className='shrink-0'
          onClick={() => {
            if (isArmed) { onRemove(word.id); setArmedWordKey(null); }
            else { setArmedWordKey(armedKey); }
          }}
          onPointerLeave={(e) => { if (e.pointerType !== 'touch' && isArmed && armSettled()) setArmedWordKey(null); }}
          onBlur={() => { if (isArmed) setArmedWordKey(null); }}
          onKeyDown={(e) => { if (e.key === 'Escape' && isArmed) { e.stopPropagation(); setArmedWordKey(null); } }}
        >
          <Trash2 className='h-4 w-4' />
        </IconButton>
      </div>

      {/*
        * BELOW THE ROW, not inside it. Beside the fields it squeezed the answer to make room
        * for a squeezed warning, which helps nobody — owner: "squishing the actual answer to
        * put a squished warning is not helpful". The wrapper carries the sortable ref and the
        * field id so the row keeps being draggable and reachable from the issue list; the
        * armed paint stays on the row, which is the thing being deleted.
        */}
      {isArmed && strandWarning && <StrandWarning text={strandWarning} />}
    </div>
  );
}

// ─── Sortable feedback card ───────────────────────────────────────────────────

interface SortableFeedbackCardProps extends FeedbackCardProps {
  sortId: string;
}

function SortableFeedbackCard({ sortId, ...cardProps }: SortableFeedbackCardProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: sortId });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-center gap-1.5 ${isDragging ? 'opacity-40' : ''}`}
    >
      <div
        {...listeners}
        {...attributes}
        aria-label={t('blocks.feedbackPanel.dragToReorder')}
        className='flex h-7 w-4 shrink-0 cursor-grab items-center justify-center touch-none text-ws-muted hover:text-ws-text active:cursor-grabbing focus-visible:[outline:2px_auto]'
      >
        <GripVertical className='h-4 w-4' />
      </div>
      <div className='min-w-0 flex-1'>
        <FeedbackCard fieldId={sortId} {...cardProps} />
      </div>
    </div>
  );
}

// ─── Trigger UI (flat list across every blank's non-correct words) ────────────


function FitbTriggerChip({ option }: { option: TriggerOption }) {
  const chipColor =
    chipToneClass(option.evaluation ?? 'neutral');
  return (
    <span className={`${TRIGGER_CHIP_CLASS} ${chipColor}`}>
      {option.evaluation === 'incorrect' && <X aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {option.evaluation === 'correct'   && <Check aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={3} />}
      {(option.evaluation === 'neutral' || !option.evaluation) && <Minus aria-hidden='true' className='h-3 w-3 shrink-0' strokeWidth={2} />}
      <ChipDivider />
      <span className={`${TRIGGER_CHIP_TEXT_CLASS} font-mono`}>{option.label}</span>
    </span>
  );
}

// Module-level (not per-instance) — see mcq-block.tsx's identical caches for why: same
// "recomputes on every keystroke anywhere in the task, not just this block's own fields" pattern,
// fixed uniformly across every block rather than only where it happened to be expensive enough to
// notice today. `dIds` (getBlankIdsInOrder(d.question)) is deliberately NOT part of the dep list
// below — it's a fresh array every call but fully derived from `d.question`, itself derived from
// `block.data`, so keying on `block.data` alone already invalidates correctly.
const fitbIssuesCache: IssueCache<BlockIssue[]> = new WeakMap();
const fitbTestDataCache: IssueCache<FitbTestData> = new WeakMap();
/**
 * A SECOND cache, for the draft.
 *
 * Preview's chips describe the SUBMITTED answer -- that is what the student has been told about.
 * Behind the Scenes wants the other question, "what fires if they press Submit now", and the two
 * part company the moment someone edits after submitting. Keyed on the draft, so it costs one
 * derivation per distinct answer tried rather than one per keystroke.
 */
const fitbDraftChipsCache: IssueCache<FitbTestData> = new WeakMap();

// ─── Main component ───────────────────────────────────────────────────────────

export default function FitbBlock({
  block, isPreview, previewSubmitSignal, onUpdate, onRemove, onDuplicate, onSelect, isSelected, isDragOver,
  isMain = false, embedded, onClose, mobileIsEditing, initialIsEditingFeedback, onFeedbackModeChange,
  blocks, onRename,
}: FitbBlockProps) {
  const { t } = useTranslation();
  const isPhone = useIsPhone();
  const isPhoneLandscape = useIsPhoneLandscape();
  const { blockRef, isDeleteArmed, triggerDuplicate, triggerDelete, disarmDelete } = useArmedDelete(onRemove, onDuplicate);
  const variables = useTaskVariables();
  const issueCtx = useIssueContext(block.id, blocks, variables);
  const {
    setTestVersion, shownVersionId,
    editorVersion, setEditorVersion, editorVersionId,
    ringEditorJump,
  } = useBlockVersions(block);
  /** Through the version: the block's own top-level copy is vestigial once a bank exists. */
  const initial = useRef(applyFitbVersion(initData(block.data), editorVersion));
  const shownVersions = (initData(block.data).versions ?? []) as { id: string }[];

  // Declared above the data state it decides the shape of: only the EDITING instance holds state,
  // and the collapsed card derives from props. See `use-restored-data`.
  const [isEditing, setIsEditing] = useState(embedded || (!onSelect && !initial.current.question));

  const [editedQuestionHtml, setQuestionHtml] = useState(initial.current.question);
  const [editedBlanks, setBlanks] = useState<FitbBlank[]>(initial.current.blanks);
  const [editedCaseSensitive, setCaseSensitive] = useState(initial.current.caseSensitive);
  const [editedAccentSensitive, setAccentSensitive] = useState(initial.current.accentSensitive);
  const [editedGradingMode, setGradingMode] = useState(initial.current.gradingMode);
  const [editedConditionalEquation, setConditionalEquation] = useState<string | undefined>(initial.current.conditionalEquation);
  const [editedConditionalBranches, setConditionalBranches] = useState<ConditionalRoleBranch[] | undefined>(initial.current.conditionalBranches);
  // Unchecking "Correctness depends on variables" clears the equation/branches from state — but a
  // teacher who rechecks it before closing the editor shouldn't lose what they'd typed. Holds the
  // last-known equation/branches across an off→on cycle within the same editing session; cleared
  // for real in handleDone, so it never survives an exit while the toggle is left off.
  /**
   * The branch set switched off but not yet discarded, so an accidental untick (or deleting the last
   * variable chip) doesn't destroy the work. Seeded from `conditionalBranchesDraft` on the saved
   * block, which is what lets it survive a reload as well as a toggle. Dropped on close, and that is
   * what makes the removal permanent (recovery is undo).
   */
  const savedConditionalDraftRef = useRef<FitbBlockData['conditionalBranchesDraft']>(initial.current.conditionalBranchesDraft);
  /**
   * Is the banked set OURS to offer back, or left over from a session long finished?
   *
   * `savedConditionalDraftRef` is seeded from the saved block so that closing clears it, which is
   * what garbage-collects one left behind. But offering it back is a different question. The
   * restore below already says "from the last time THIS SESSION turned the toggle off" — this is
   * what makes that true, where seeding alone had it resurrect a set banked weeks ago.
   *
   * Owner's reasoning, and it is the whole point: come back much later and a stored branching
   * correct you have long forgotten is confusing, not helpful. An untick is recoverable for as
   * long as you are still looking at the block; after that, recovery is undo.
   */
  const bankedThisSessionRef = useRef(false);
  const [editedAngleMode, setAngleMode] = useState<AngleMode>(initial.current.angleMode ?? 'degrees');
  const [activeBranchIndex, setActiveBranchIndex] = useBranchJumpTarget(block.id);

  const questionEditorRef = useRef<Editor | null>(null);
  const sharedToolbar = useSharedEditorToolbar();
  
  const { setActiveEditor, activeEditorRef } = sharedToolbar;

  /**
   * One stateful copy, not two — the card derives from props and cannot go stale. The echo
   * bookkeeping, and why its markers are state rather than refs, lives in `use-restored-data`.
   */
  const resyncFeedbackRef = useRef<((raw: unknown) => void) | null>(null);
  const edited = {
    question: editedQuestionHtml, blanks: editedBlanks, caseSensitive: editedCaseSensitive,
    accentSensitive: editedAccentSensitive, gradingMode: editedGradingMode,
    conditionalEquation: editedConditionalEquation, conditionalBranches: editedConditionalBranches,
    angleMode: editedAngleMode,
  };
  const restored = useRestoredData<FitbBlockData>({
    raw: block.data,
    isEditing,
    derive: (raw) => applyFitbVersion(initData(raw as Parameters<typeof initData>[0]), editorVersion),
    current: { ...applyFitbVersion(initData(block.data), editorVersion), ...edited } as FitbBlockData,
    apply: seedEditorFrom,
  });

  /** Push one version's content into the editor's local state — an undo, another device's save
   *  and switching version are the same act from here. */
  function seedEditorFrom(next: FitbBlockData) {
      setQuestionHtml(next.question);
      setBlanks(next.blanks);
      setCaseSensitive(next.caseSensitive);
      setAccentSensitive(next.accentSensitive);
      setGradingMode(next.gradingMode);
      setConditionalEquation(next.conditionalEquation);
      setConditionalBranches(next.conditionalBranches);
      setAngleMode(next.angleMode ?? 'degrees');
      savedConditionalDraftRef.current = next.conditionalBranchesDraft;
      resyncFeedbackRef.current?.(next.feedback);
  }

  const seededVersionRef = useVersionSeeding(editorVersion, (id) => {
    seedEditorFrom(applyFitbVersion(initData(block.data), id));
  });

  /**
   * Switch which version the EDITOR is pointed at, re-seeding every field from it.
   *
   * `from` is the data to read that version OUT of. It defaults to the block's own, which is right
   * everywhere except the two places it matters most: during an add or a remove, `block.data` is
   * still the PRE-change data, the new version's id is not in it, and `pickVersion` answers an id
   * it cannot find with `versions[0]`. So pressing "+" seeded the editor from VERSION 1 while the
   * pager read "Version N of N", and the copy only became wrong once the teacher typed into it.
   * A caller holding the post-change data passes it.
   */
  const selectVersion = (id: string | undefined, from = initData(block.data)) => {
    seededVersionRef.current = id;
    setEditorVersion(id);
    seedEditorFrom(applyFitbVersion(from, id));
  };

  /** Open the editor on what the test panel is showing. The one bridge between the pointers. */
  const editShownVersion = (branchId?: string) => {
    if (shownVersionId) selectVersion(shownVersionId);
    void ringEditorJump(branchId);
  };
  const questionHtml = isEditing ? editedQuestionHtml : restored.data.question;
  const blanks = isEditing ? editedBlanks : restored.data.blanks;
  const caseSensitive = isEditing ? editedCaseSensitive : restored.data.caseSensitive;
  const accentSensitive = isEditing ? editedAccentSensitive : restored.data.accentSensitive;
  const gradingMode = isEditing ? editedGradingMode : restored.data.gradingMode;
  const conditionalEquation = isEditing ? editedConditionalEquation : restored.data.conditionalEquation;
  const conditionalBranches = isEditing ? editedConditionalBranches : restored.data.conditionalBranches;
  const angleMode = isEditing ? editedAngleMode : (restored.data.angleMode ?? 'degrees');

  /** Every write out of this block, so its own echo is never mistaken for an undo. */
  const emit = (data: Record<string, unknown>) => { restored.markEmitted(data); onUpdate(data); };

  /* One version per row of the driver's answer space. Nothing about this block is
     named here: `addFitbVersion` already knows its answer key, which is what keeps this
     to one line in twelve places. See `useDrivenVersionProjection`. */
  useDrivenVersionProjection({ block, data: initData(block.data), emit, addVersion: addFitbVersion });

  /** Every formula field in this block, wherever it sits — see `AngleModeProvider`. The merge
   *  lives in the hook so no block can delete its siblings' modes by writing only its own, and
   *  the spread is `block.data` rather than the editor's view, which is `initData`'s output and
   *  omits whatever a block threads separately — numerical's feedback, famously. */
  const angleModeProps = useBlockAngleModes(
    block.data,
    patch => emit({ ...block.data, ...patch }),
  );

  /**
   * EVERY field write goes through here: `onUpdate` REPLACES `block.data`, so a write that builds
   * its object field by field deletes everything it forgot to name. Taking a PARTIAL is the point.
   */
  const emitFields = (next: Partial<FitbBlockData>) => {
    const base = initData(block.data);
    emit(writeFitbVersion(base, editorVersion, { ...base, ...next } as FitbBlockData) as unknown as Record<string, unknown>);
  };

  // Transient equation defects wait for the equation to stop changing. Two filters, not one: the
  // card and test panel read an issue list built from `block.data`, the editor panel reads one
  // built from live state, and the two are often rendered by different instances of this
  // component — so each must be gated by the settling of the value it was computed from.
  // No `answers` here: this block's answers are not equations, only its branching selector is.
  const { settleLive, settleStored } = useIssueSettling(
    { equation: conditionalEquation },
    { equation: initData(block.data).conditionalEquation },
  );

  const currentDataRef = useRef({ questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, conditionalEquation, conditionalBranches, angleMode });
  currentDataRef.current = { questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, conditionalEquation, conditionalBranches, angleMode };

  const idsInOrder = getBlankIdsInOrder(questionHtml);
  const visibleBlanks: FitbBlank[] = idsInOrder.map(id => blanks.find(b => b.id === id) ?? makeBlank(id));

  // Role-per-word for whichever branch is active — same construction the blank-editing UI below
  // builds inline for its own display; centralized here so title-collision/staleness checks
  // against the active branch's own feedback can share it too. Gated on gradingMode itself, not
  // just conditionalBranches existing — branches are authored exclusively in graded mode.
  const activeBranch = gradingMode === 'graded' && conditionalBranches && conditionalBranches.length > 0
    ? conditionalBranches[Math.min(activeBranchIndex, conditionalBranches.length - 1)]
    : undefined;
  // Feedback is stale (suppressed, not deleted) once its target word is marked correct.
  const wordRoleByTrigger = new Map<string, FitbBlankWord['role']>();
  for (const blank of blanks) for (const word of blank.words) wordRoleByTrigger.set(buildFitbWordTrigger(blank.id, word.id), word.role);

  // The positive-slot's three known default bodies — see retriggerPositiveSlot's doc comment.
  const positiveSlotContent = {
    correct: `<p>${t('blocks.feedbackDefaults.correctContent')}</p>`,
    anyAnswer: `<p>${t('blocks.feedbackDefaults.anyAnswerContent')}</p>`,
    anyResponse: `<p>${t('blocks.feedbackDefaults.anyResponseContent')}</p>`,
  };
  const positiveSlotTitle =
    gradingMode === 'graded' ? t('blocks.feedbackDefaults.correctTitle') :
    gradingMode === 'survey' ? t('blocks.feedbackDefaults.anyAnswerTitle') :
    t('blocks.feedbackDefaults.anyResponseTitle');

  const { feedback, setFeedback, resyncFeedback, getTitle } = useInputBlockFeedback({
    initialRaw: initial.current.feedback,
    defaults: {
      unansweredTitle: t('blocks.feedbackDefaults.noAnswerTitle'),
      unansweredContent: `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>`,
      incorrectTitle: t('blocks.feedbackDefaults.incorrectTitle'),
      incorrectContent: `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>`,
      correctTitle: positiveSlotTitle,
      correctContent: gradingMode === 'graded' ? positiveSlotContent.correct : gradingMode === 'survey' ? positiveSlotContent.anyAnswer : positiveSlotContent.anyResponse,
    },
    titleLabels: {
      unanswered: t('blocks.feedbackDefaults.noAnswerTitle'),
      correct: positiveSlotTitle,
      incorrect: t('blocks.feedbackDefaults.incorrectTitle'),
      extra: { incomplete_answer: t('blocks.feedbackDefaults.incompleteAnswerTitle') },
    },
    extraDefaults: [{
      trigger: 'incomplete_answer',
      title: t('blocks.feedbackDefaults.incompleteAnswerTitle'),
      content: `<p>${t('blocks.feedbackDefaults.incompleteAnswerContent')}</p>`,
      evaluation: 'neutral',
    }],
    positiveTrigger: positiveTriggerForMode(gradingMode),
    onUpdate: (nextFeedback) => {
      const d = currentDataRef.current;
      emitFields({ question: d.questionHtml, blanks: d.blanks, caseSensitive: d.caseSensitive, accentSensitive: d.accentSensitive, gradingMode: d.gradingMode, feedback: nextFeedback, conditionalEquation: d.conditionalEquation, conditionalBranches: d.conditionalBranches, angleMode: d.angleMode });
    },
  });

  resyncFeedbackRef.current = resyncFeedback;
  // ── Branch-scoped feedback indirection ──────────────────────────────────────
  // Every read/write below that deals with feedback CONTENT goes through these instead of
  // `feedback`/`setFeedback` directly, so the same JSX-construction code works unchanged whether
  // branching is on or off — see Dropdown/Keyword's identical indirection layer for the full
  // reasoning.
  const scenario = useScenarioFollower<ScenarioRoleBranch<{ states: FeedbackState[] }>>({
    block, blocks, gradingMode, feedback, isEditing, isPreview,
    storedData: initData(block.data) as never,
    restoredRows: restored.data.scenarioBranches,
    initialRows: initial.current.scenarioBranches,
    emit: data => emit(versionStructure(block.data, data)),
    applyVersion: applyFitbVersion,
    emitFields: next => emitFields(next as Partial<FitbBlockData>),
    /* One flat `Record<wordId, role>` across every blank, exactly as the numeric branch
       carries — a word id is unique across the whole block, so the row knows nothing about
       the nesting. */
    rowTemplate: () => ({
      roles: Object.fromEntries(visibleBlanks.flatMap(b => b.words.map(w => [w.id, w.role]))),
      feedback,
    }),
    rowFromVersion: v => ({
      roles: (v as unknown as { roles: Record<string, FitbBlankWord['role']> }).roles,
      feedback: (v as unknown as { feedback: { states: FeedbackState[] } }).feedback,
    }),
    numeric: {
      read: () => ({ equation: conditionalEquation, branches: conditionalBranches }),
      write: (eq, br) => {
        setConditionalEquation(eq as string | undefined);
        setConditionalBranches(br as ConditionalRoleBranch[] | undefined);
      },
    },
  });

  /* WHICHEVER setup the panel is editing — a numeric branch and a scenario row differ only in
     what SELECTS them, and every read below this line is one path. */
  const activeSetup = scenario.activeScenario ?? activeBranch;
  const activeFeedbackStates = activeSetup ? activeSetup.feedback.states : feedback.states;

  const effectiveBlanksForActive: FitbBlank[] = (activeSetup
    ? visibleBlanks.map(b => ({ ...b, words: b.words.map(w => ({ ...w, role: activeSetup.roles[w.id] ?? ('neutral' as const) })) }))
    : visibleBlanks
  ).map(b => ({ ...b, words: rolesForMode(gradingMode, b.words) }));

  function setActiveFeedbackStates(nextStates: FeedbackState[]) {
    if (activeBranch) {
      const nextBranches = (conditionalBranches ?? []).map(b => b.id === activeBranch.id ? { ...b, feedback: { states: nextStates } } : b);
      setConditionalBranches(nextBranches);
      push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, nextBranches, conditionalEquation, angleMode);
    } else {
      const next = { states: nextStates };
      setFeedback(next);
      push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, next, conditionalBranches, conditionalEquation, angleMode);
    }
  }

  function updateActiveFeedbackState(id: string, patch: Partial<FeedbackState>) {
    setActiveFeedbackStates(activeFeedbackStates.map(s => (s.id === id ? { ...s, ...patch } : s)));
  }

  function deleteActiveFeedbackState(id: string) {
    const state = activeFeedbackStates.find(s => s.id === id);
    if (!state || state.type === 'default') return;
    setActiveFeedbackStates(activeFeedbackStates.filter(s => s.id !== id));
  }

  function isTargetedStaleForActive(s: FeedbackState): boolean {
    if (s.type !== 'targeted') return false;
    const parsed = parseFitbWordTrigger(s.trigger);
    if (!parsed) return false;
    const blank = effectiveBlanksForActive.find(b => b.id === parsed.blankId);
    return blank?.words.find(w => w.id === parsed.wordId)?.role === 'correct';
  }

  // ── Live in-editor issues ────────────────────────────────────────────────────
  // The exact same computation the collapsed bar (and the test panel's own diagnostics list) uses,
  // run against the editor's own live state instead of `initData(block.data)` — see fitbIssues'
  // own doc comment.
  /* Enumerated, so a field left out is a check that silently never runs — `scenarioBranches`
     is what makes a follower's live test grade against its row rather than its own key. */
  const liveD: FitbBlockData = { question: questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation, conditionalBranches, angleMode, followsBlockId: initData(block.data).followsBlockId, scenarioBranches: scenario.rows };
  const editorIssues = settleLive(fitbIssues(liveD, { ...issueCtx, blankIdsInOrder: idsInOrder }));
  // ONE derivation, read from the issues themselves rather than re-running the duplicate check to
  // decide which rows to ring — the pattern `BlockIssue.fields` exists for, already used by
  // keyword/dropdown/mcq/ordering. The check has always populated `fields`; nothing consumed it
  // here, so the panel named a collision and no field showed which words it meant.
  const collidingWordIds = issueFieldIds(editorIssues);
  // missing-feedback-text is excluded because it already has its own more specific home (each
  // blank feedback card shows its own warning directly) — see keywordIssues' identical exclusion
  // for the full reasoning.

  useLayoutEffect(() => { if (mobileIsEditing !== undefined) setIsEditing(mobileIsEditing); }, [mobileIsEditing]);
  const collapsedContentRef = useRef<HTMLDivElement>(null);
  const shouldFocusCollapsed = useRef(false);
  const shouldFocusEditor = useRef(false);
  useLayoutEffect(() => {
    if (!isEditing && shouldFocusCollapsed.current) {
      shouldFocusCollapsed.current = false;
      collapsedContentRef.current?.focus();
    }
  }, [isEditing]);

  const [armedWordKey, setArmedWordKey] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 15 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 12 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  // Keyboard-only focus lands on <body> once a branch's own remove button unmounts — a stable,
  // always-present fallback to refocus, so a keyboard/screen-reader user's place in the editor
  // isn't lost on every delete.
  const branchPrevButtonRef = useRef<HTMLButtonElement | null>(null);

  // ── Live test state ───────────────────────────────────────────────────────
  const [ownRolledValues, setRolledValues] = useState<Record<string, string>>({});
  // Preview must show ONE task-level roll shared by every block (see TaskPreviewRollContext): a task
  // whose blocks all read {n} has to agree about n, and seeding Preview from this block's own roll made
  // them disagree. Block-level testing keeps its private roll plus reroll, which is the whole point of
  // it — a teacher steps through combinations for the one block they're testing.
  const taskPreviewRoll = useTaskPreviewRoll();
  const rolledValues = isPreview ? (taskPreviewRoll ?? {}) : ownRolledValues;
  /**
   * Seeded from the last attempt on a revisit -- see `usePriorAnswer`. Walking back into a task
   * does not wipe it, and this is the half that was missing: the counts and the roll already
   * survived, so a student sent to remediation and back kept their leniency but lost their work.
   */
  const prior = usePriorAnswer<Record<string, string>>();
  const [testValues, setTestValues] = useState<Record<string, string>>(prior ?? {});
  const [submittedValues, setSubmittedValues] = useState<Record<string, string>>(prior ?? {});
  const [hasSubmitted, setHasSubmitted] = useState(prior !== undefined);
  // The SUBMITTED value, never the draft: recording the draft would make the attempt a snapshot
  // of the student's typing rather than of what they chose to hand in.
  useReportAnswer(hasSubmitted ? submittedValues : undefined);
  const [continuous, setContinuous] = useState(true);
  const [phoneTestMode, setPhoneTestMode] = useState<'edit' | 'test'>('edit');

  useEffect(() => {
    // Also seeded for `isPreview` — the student-facing Preview render needs a real rolled value
    // exactly like the isSelected test widget does.
    if (isSelected) {
      setRolledValues(rollVariableValues(variables));
    // NOT in Preview. This branch exists for the EDITOR, where deselecting a block clears the
      // test widget it was driving. Preview has no selection, so `isSelected` is false forever and
      // this ran on mount, wiping the answer a revisit had just restored. Preview's own "start
      // again" is the remount `previewResetKey` forces, not this.
      } else if (!isPreview) {
      setTestValues({});
      setSubmittedValues({});
      setHasSubmitted(false);
      setContinuous(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSelected, isPreview]);

  function handleTestChange(blankId: string, text: string) {
    const next = { ...testValues, [blankId]: text };
    setTestValues(next);
    // `&& !isPreview` — continuous testing is a TEACHER affordance (the toggle lives in the test
    // bar), and Preview reuses this same change handler for the student's input. Without the
    // guard, typing an answer graded it instantly: feedback appeared before Submit was ever
    // clicked, which is not what a student sees. Preview submits through previewSubmitSignal
    // and nothing else.
    if (continuous && !isPreview) { setSubmittedValues(next); setHasSubmitted(true); }
  }
  function handleContinuousChange(next: boolean) {
    setContinuous(next);
    if (next) { setSubmittedValues(testValues); setHasSubmitted(true); }
  }
  function handleTestSubmit() {
    setSubmittedValues(testValues);
    setHasSubmitted(true);
  }
  function handleTestReset() {
    setTestValues({});
    setSubmittedValues({});
    setHasSubmitted(false);
  }
  // A result belongs to the version it was taken on; stepping the pager retires it.
  useTestResetOnVersionChange(shownVersionId, handleTestReset);
  function handleTestReroll() {
    setRolledValues(rollVariableValues(variables));
  }

  // Preview's single bottom Submit (see task-content.tsx's own doc comment on the signal) —
  // reacts to the signal CHANGING, not a per-block Submit click, since Preview has no per-block
  // Submit button any more. Reuses the exact same testValues/hasSubmitted state (and therefore the
  // exact same feedback-firing machinery) the live test widget already uses — Preview is just a
  // different trigger for the same evaluation, not a separate one.
  useEffect(() => {
    if (isPreview && previewSubmitSignal) {
      setSubmittedValues(testValues);
      setHasSubmitted(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSubmitSignal]);

  function push(
    qHtml = questionHtml, blks = blanks, cs = caseSensitive, as = accentSensitive, mode = gradingMode, fdbk = feedback,
    cb = conditionalBranches, eq = conditionalEquation, angMode = angleMode,
  ) {
    // The retained draft rides along on every ordinary save, so editing anything else cannot
    // drop it. Deliberately not a parameter: a defaulted parameter cannot express "clear this".
    emitFields({ question: qHtml, blanks: blks, caseSensitive: cs, accentSensitive: as, gradingMode: mode, feedback: fdbk, conditionalEquation: eq, conditionalBranches: cb, conditionalBranchesDraft: savedConditionalDraftRef.current, angleMode: angMode });
  }

  /**
   * Persists branching as CLEARED. Needed because `push` cannot express it: a JS default parameter
   * fires on `undefined`, so `push(..., undefined, undefined)` does not clear the branches, it
   * restores `cb = conditionalBranches` — the very values being removed. Toggling branching off
   * therefore looked like it worked (the UI reads local state, already set to undefined) while the
   * saved block kept its branches: the canvas card, which grades off the saved block, went on
   * resolving a branch the editor said was off, and reopening the block showed the toggle back on.
   *
   * The authored set is still recoverable for as long as this editor stays open — the draft is held
   * in `savedConditionalDraftRef` and re-ticking the box restores it. Closing drops the draft, and
   * from there recovery is undo.
   */
  /**
   * Every write that changes the branch state itself, stated explicitly. It cannot go through `push`:
   * a JS default parameter fires on `undefined`, so `push(..., undefined, undefined)` restores
   * `cb = conditionalBranches` rather than clearing it — the trap that made "turn branching off"
   * silently never stick, leaving the canvas card grading against branches the editor said were off.
   */
  function pushBranchState(
    branches: ConditionalRoleBranch[] | undefined,
    equation: string | undefined,
    draft: FitbBlockData['conditionalBranchesDraft'],
  ) {
    emitFields({ question: questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, conditionalEquation: equation, conditionalBranches: branches, conditionalBranchesDraft: draft, angleMode });
  }

  function handleGradingMode(mode: GradingMode) {
    const nextFeedbackStates = retriggerPositiveSlot(feedback.states, mode, positiveSlotContent);
    if (nextFeedbackStates !== feedback.states) setFeedback({ states: nextFeedbackStates });

    setGradingMode(mode);
    push(questionHtml, blanks, caseSensitive, accentSensitive, mode, { states: nextFeedbackStates });
  }

  function handleCaseSensitive(val: boolean) {
    setCaseSensitive(val);
    push(questionHtml, blanks, val, accentSensitive, gradingMode);
  }

  function handleAccentSensitive(val: boolean) {
    setAccentSensitive(val);
    push(questionHtml, blanks, caseSensitive, val, gradingMode);
  }

  /** Applies a resync/filter transform to EVERY independent feedback set a FITB block has — the
   * top-level one plus each branch's own — since a blank/word's text, position, or removal affects
   * every targeted feedback that references it, not just whichever branch happens to be active in
   * the scroller right now. Mirrors Dropdown/Keyword's identical helper. */
  function updateAllFeedbackSets(
    transform: (states: FeedbackState[]) => FeedbackState[],
  ): { feedback: FitbBlockData['feedback']; branches: ConditionalRoleBranch[] | undefined } {
    return {
      feedback: { states: transform(feedback.states) },
      branches: conditionalBranches?.map(b => ({ ...b, feedback: { states: transform(b.feedback.states) } })),
    };
  }

  // Reconciles blanks[] against the passage's actual chips whenever the question changes —
  // covers insertion (new chip -> new blank config), deletion (backspaced chip -> drop its
  // config and any tied feedback), undo/redo, and paste. Mirrors Ordering's
  // OrderingCustomOrderEditor "self-heals against drift" and Keyword's removeOption cleanup.
  function handleQuestionChange(html: string) {
    setQuestionHtml(html);
    const idsInOrderNext = getBlankIdsInOrder(html);
    const idsInHtml = new Set(idsInOrderNext);
    const nextBlanks = [
      ...blanks.filter(b => idsInHtml.has(b.id)),
      ...idsInOrderNext.filter(id => !blanks.some(b => b.id === id)).map(id => makeBlank(id)),
    ];
    const removedIds = new Set(blanks.filter(b => !idsInHtml.has(b.id)).map(b => b.id));
    // Reordering/adding/removing blanks in the passage shifts every surviving blank's ordinal,
    // which every one of their feedback titles embeds — resync against the NEW question order.
    // Applied across every branch's own feedback too, not just the top-level one.
    const nextVisibleBlanks = idsInOrderNext.map(id => nextBlanks.find(b => b.id === id) ?? makeBlank(id));
    const { feedback: nextFeedback, branches: nextBranchesFromFeedback } = updateAllFeedbackSets(states => {
      const surviving = removedIds.size === 0 ? states : states.filter(s => {
        if (s.type !== 'targeted') return true;
        const parsed = parseFitbWordTrigger(s.trigger);
        return !parsed || !removedIds.has(parsed.blankId);
      });
      return resyncFitbTargetedTitles(nextVisibleBlanks, surviving, t);
    });
    // A branch's OWN `roles` map isn't touched by the reassignment above — any now-meaningless
    // stale entries for a removed blank's words are dropped.
    const removedWordIds = new Set(blanks.filter(b => removedIds.has(b.id)).flatMap(b => b.words.map(w => w.id)));
    const nextBranches = nextBranchesFromFeedback?.map(b => ({
      ...b,
      roles: Object.fromEntries(Object.entries(b.roles).filter(([wordId]) => !removedWordIds.has(wordId))),
    }));
    setBlanks(nextBlanks);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(html, nextBlanks, caseSensitive, accentSensitive, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  function insertBlank() {
    questionEditorRef.current?.chain().focus().insertContent({ type: 'blankChip', attrs: { id: crypto.randomUUID() } }).run();
  }

  function updateWordText(blankId: string, wordId: string, text: string) {
    const next = blanks.map(b => b.id === blankId ? { ...b, words: b.words.map(w => w.id === wordId ? { ...w, text } : w) } : b);
    // Keep any feedback targeting this word's title in sync with its new text — otherwise
    // renaming "cat" to "feline" would leave a stale "Answer 1: cat" title behind. Applied across
    // every branch's own feedback too, not just whichever is active.
    const nextVisibleBlanks = idsInOrder.map(id => next.find(b => b.id === id) ?? makeBlank(id));
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncFitbTargetedTitles(nextVisibleBlanks, states, t)
    );
    setBlanks(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, next, caseSensitive, accentSensitive, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  /** Static (no-branch) role cycle — used only when `!activeBranch`; a branch's own role click
   * routes to handleBranchWordRoleClick instead. */
  function handleWordRoleClick(blankId: string, wordId: string) {
    const CYCLE: FitbBlankWord['role'][] = ['neutral', 'correct', 'incorrect'];
    const next = blanks.map(b => b.id === blankId ? {
      ...b,
      words: b.words.map(w => {
        if (w.id !== wordId) return w;
        const idx = CYCLE.indexOf(w.role);
        return { ...w, role: CYCLE[(idx + 1) % CYCLE.length]! };
      }),
    } : b);
    // Every targeted feedback's evaluation mirrors its target word's role — keep them in sync
    // whenever a role changes, rather than letting the two silently drift apart.
    const nextFeedback = { states: resyncFitbTargetedEvaluations(next, feedback.states) };
    setBlanks(next);
    setFeedback(nextFeedback);
    push(questionHtml, next, caseSensitive, accentSensitive, gradingMode, nextFeedback);
  }

  /** Simpler than MCQ/Dropdown's version — no `allowMultiple` fork needed at all: FITB has no
   * select-mode toggle, and multiple correct words per blank is a normal, intended pattern
   * (synonyms), so a branch role click just cycles neutral→correct→incorrect for that one word,
   * unconditionally, same as the static (no-branch) role cycle. */
  /** The three-way cycle plus its feedback resync, shared by the numeric branch and the
   *  scenario row so the two cannot disagree about what clicking the same word does. */
  function rolesAfterWordClick(roles: Record<string, FitbBlankWord['role']>, wordId: string) {
    const CYCLE: FitbBlankWord['role'][] = ['neutral', 'correct', 'incorrect'];
    const cur = roles[wordId] ?? 'neutral';
    return { ...roles, [wordId]: CYCLE[(CYCLE.indexOf(cur) + 1) % CYCLE.length]! };
  }
  function resyncedForRoles(roles: Record<string, FitbBlankWord['role']>, states: FeedbackState[]) {
    return resyncFitbTargetedEvaluations(
      visibleBlanks.map(bl => ({ ...bl, words: bl.words.map(w => ({ ...w, role: roles[w.id] ?? ('neutral' as const) })) })),
      states);
  }

  function handleBranchWordRoleClick(branchId: string, wordId: string) {
    const next = (conditionalBranches ?? []).map(b => {
      if (b.id !== branchId) return b;
      const nextRoles = rolesAfterWordClick(b.roles, wordId);
      return { ...b, roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, b.feedback.states) } };
    });
    setConditionalBranches(next);
    push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  /** The same act keyed by variant. Deciding a row's roles IS reviewing it. */
  function handleScenarioWordRoleClick(wordId: string) {
    const row = scenario.activeScenario;
    if (!row) return;
    const nextRoles = rolesAfterWordClick(row.roles, wordId);
    scenario.writeActiveRow({
      roles: nextRoles, feedback: { states: resyncedForRoles(nextRoles, row.feedback.states) },
    } as never);
  }

  function addWord(blankId: string) {
    const newWord = makeWord();
    const next = blanks.map(b => b.id === blankId ? { ...b, words: [...b.words, newWord] } : b);
    // A new word needs a role entry in every existing branch too, or its role would silently read
    // as `undefined` (falling back to 'neutral' in effectiveBlanksForActive, but never actually
    // present in the data) until a teacher happens to click it.
    const nextBranches = conditionalBranches?.map(b => ({ ...b, roles: { ...b.roles, [newWord.id]: 'neutral' as const } }));
    setBlanks(next);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, next, caseSensitive, accentSensitive, gradingMode, feedback, nextBranches ?? conditionalBranches);
  }

  function removeWord(blankId: string, wordId: string) {
    const blank = blanks.find(b => b.id === blankId);
    if (!blank || blank.words.length <= 1) return;
    const nextBlanks = blanks.map(b => b.id === blankId ? { ...b, words: b.words.filter(w => w.id !== wordId) } : b);
    // Deleting a word also deletes any feedback tied to it — recoverable only through undo.
    // Remaining feedback titles are resynced too, since a blank-fallback-labeled word's position
    // ("Word 2") shifts for every word after the removed one in the same blank. Applied across
    // every branch's own feedback, not just whichever is active — a word disappearing affects all
    // of them.
    const nextVisibleBlanks = idsInOrder.map(id => nextBlanks.find(b => b.id === id) ?? makeBlank(id));
    const { feedback: nextFeedbackFromSets, branches: nextBranchesFromFeedback } = updateAllFeedbackSets(
      states => resyncFitbTargetedTitles(nextVisibleBlanks, states.filter(s => !(s.type === 'targeted' && s.trigger === buildFitbWordTrigger(blankId, wordId))), t)
    );
    // A branch's OWN `roles` map isn't touched by the reassignment above — the now-meaningless
    // stale entry for the removed word is dropped. No auto-promotion of a replacement correct word
    // here (unlike Dropdown/MCQ single-select) — multiple (or zero) correct words per blank is a
    // normal, valid state for FITB, so there's no single "right" substitute to pick.
    const nextBranches = nextBranchesFromFeedback?.map(b => ({
      ...b,
      roles: Object.fromEntries(Object.entries(b.roles).filter(([wId]) => wId !== wordId)),
    }));
    setBlanks(nextBlanks);
    setFeedback(nextFeedbackFromSets);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, nextBlanks, caseSensitive, accentSensitive, gradingMode, nextFeedbackFromSets, nextBranches ?? conditionalBranches);
  }

  function handleWordDragEnd(blankId: string, { active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const blank = blanks.find(b => b.id === blankId);
    if (!blank) return;
    const from = blank.words.findIndex(w => w.id === active.id);
    const to = blank.words.findIndex(w => w.id === over.id);
    if (from === -1 || to === -1) return;
    const next = blanks.map(b => b.id === blankId ? { ...b, words: arrayMove(b.words, from, to) } : b);
    // Reordering words shifts any blank-fallback-labeled word's position ("Word 2") — resync,
    // across every branch's own feedback too.
    const nextVisibleBlanks = idsInOrder.map(id => next.find(b => b.id === id) ?? makeBlank(id));
    const { feedback: nextFeedback, branches: nextBranches } = updateAllFeedbackSets(
      states => resyncFitbTargetedTitles(nextVisibleBlanks, states, t)
    );
    setBlanks(next);
    setFeedback(nextFeedback);
    if (nextBranches) setConditionalBranches(nextBranches);
    push(questionHtml, next, caseSensitive, accentSensitive, gradingMode, nextFeedback, nextBranches ?? conditionalBranches);
  }

  function rebuildStates(targeteds: FeedbackState[]): FeedbackState[] {
    const defaults = activeFeedbackStates.filter(s => s.type === 'default');
    return [
      ...defaults.filter(s => s.trigger === 'unanswered'),
      ...defaults.filter(s => s.trigger === 'incomplete_answer'),
      ...targeteds,
      ...defaults.filter(s => s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'),
      ...defaults.filter(s => s.trigger === 'incorrect'),
    ];
  }

  /** The card the Add button last produced — the one thing that opens a feedback card on mount.
   *  State, not a ref: it is read during render, and a ref mutated in a discarded render keeps a
   *  value that render threw away. */
  /**
   * The card the Add button last produced — the one thing that opens a feedback card on mount.
   *
   * Keyed BY MODE. Reordering the panel's sections on a mode switch remounts these cards, and a
   * bare id would still name one of them, so the last-added card sprang open on every switch —
   * the behaviour this was meant to remove. State, not a ref: it is read during render, and a ref
   * mutated in a discarded render keeps a value that render threw away.
   */
  const [justAddedStateId, setJustAddedStateId] = useState<string | null>(null);
  const justAddedKey = (id: string) => `${gradingMode}:${id}`;

  function addTargetedState() {
    const usedTriggers = new Set(activeFeedbackStates.filter(s => s.type === 'targeted').map(s => s.trigger));
    // The blank's own "left empty" first, per blank — the same "a blank is one of the answers"
    // rule the single-pick blocks apply at block level.
    if (gradingMode === 'optional') {
      const emptyFor = effectiveBlanksForActive.findIndex(
        b => !usedTriggers.has(buildFitbWordTrigger(b.id, FITB_BLANK_EMPTY)));
      if (emptyFor !== -1) {
        const blank = effectiveBlanksForActive[emptyFor]!;
        const newId = crypto.randomUUID();
        setJustAddedStateId(justAddedKey(newId));
        setActiveFeedbackStates(rebuildStates([...activeFeedbackStates.filter(x => x.type === 'targeted'), {
          id: newId, type: 'targeted',
          trigger: buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY),
          title: t('blocks.fitb.triggerOptionLabel', {
            ordinal: emptyFor + 1, word: t('blocks.feedbackDefaults.noResponseTitle'),
          }),
          content: '', evaluation: 'neutral',
        }]));
        return;
      }
    }
    let target: { blank: FitbBlank; blankIdx: number; word: FitbBlankWord; wordIdx: number } | null = null;
    for (let blankIdx = 0; blankIdx < effectiveBlanksForActive.length; blankIdx++) {
      const blank = effectiveBlanksForActive[blankIdx]!;
      const wordIdx = blank.words.findIndex(w => w.role !== 'correct' && !usedTriggers.has(buildFitbWordTrigger(blank.id, w.id)));
      if (wordIdx !== -1) { target = { blank, blankIdx, word: blank.words[wordIdx]!, wordIdx }; break; }
    }
    if (!target) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const newId = crypto.randomUUID();
    setJustAddedStateId(justAddedKey(newId));
    const newState: FeedbackState = {
      id: newId, type: 'targeted',
      title: fitbTargetedTitle(target.blankIdx, target.word, target.wordIdx, t),
      trigger: buildFitbWordTrigger(target.blank.id, target.word.id),
      content: '', evaluation: target.word.role === 'neutral' ? 'neutral' : 'incorrect',
    };
    setActiveFeedbackStates(rebuildStates([...targeteds, newState]));
  }

  function handleFeedbackTargetedDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const targeteds = activeFeedbackStates.filter(s => s.type === 'targeted');
    const from = targeteds.findIndex(s => s.id === String(active.id));
    const to = targeteds.findIndex(s => s.id === String(over.id));
    if (from === -1 || to === -1) return;
    setActiveFeedbackStates(rebuildStates(arrayMove(targeteds, from, to)));
  }

  /** Backfills default feedback text/titles when a fresh branch's own body is cloned from the
   * top-level feedback that was active the moment branching was first turned on — fresh ids so
   * updating branch 1's Correct card can never ambiguously also match the top-level Correct card
   * sharing the same id. Mirrors Dropdown/Keyword's identical `cloneFeedbackWithFreshIds`. */
  function cloneFeedbackWithFreshIds(source: FitbBlockData['feedback']): FitbBlockData['feedback'] {
    return { states: source.states.map(s => ({ ...s, id: crypto.randomUUID() })) };
  }

  /** Seeds every branch after the first from whichever branch was active when "add" was clicked
   * (always the last one) — structure carries over, content resets to blank if customized, or
   * carries forward unchanged if it still matches that slot's default. Was NOT actually mirroring
   * Dropdown/MCQ's identical `seedBranchFeedback` despite the doc comment's claim — this used to
   * carry forward ANY non-empty content verbatim (including a teacher's own customized
   * targeted-feedback text), silently duplicating a message written for one branch into every
   * branch forked afterward. Fixed to actually call resetContentIfCustomized, matching Dropdown/MCQ
   * — the one addition here is still the `incomplete_answer` case, which neither has a trigger for
   * at all. */
  function seedBranchFeedback(source: FitbBlockData['feedback']): FitbBlockData['feedback'] {
    return {
      states: source.states.map(s => {
        const defaultContent = s.type !== 'default' ? '' :
          s.trigger === 'unanswered' ? `<p>${t('blocks.feedbackDefaults.noAnswerContent')}</p>` :
          s.trigger === 'incomplete_answer' ? `<p>${t('blocks.feedbackDefaults.incompleteAnswerContent')}</p>` :
          s.trigger === 'incorrect' ? `<p>${t('blocks.feedbackDefaults.incorrectContent')}</p>` :
          // Branching only exists in graded mode, so the positive slot inside a branch is always
          // the 'correct' trigger, never any_answer/any_response.
          positiveSlotContent.correct;
        return { ...s, id: crypto.randomUUID(), content: resetContentIfCustomized(s.content, defaultContent) };
      }),
    };
  }

  function makeConditionalBranch(seedFrom: FitbBlockData['feedback'], mode: 'migrate' | 'fork'): ConditionalRoleBranch {
    const allWordIds = visibleBlanks.flatMap(b => b.words.map(w => [w.id, w.role] as const));
    return {
      id: crypto.randomUUID(),
      operator: '==',
      threshold: '0',
      roles: Object.fromEntries(allWordIds),
      feedback: mode === 'migrate' ? cloneFeedbackWithFreshIds(seedFrom) : seedBranchFeedback(seedFrom),
    };
  }

  function toggleConditionalCorrectness(enabled: boolean) {
    if (enabled) {
      // Restore the draft from the last time this session turned the toggle off, if any —
      // otherwise start fresh, same as before.
      const draft = bankedThisSessionRef.current ? savedConditionalDraftRef.current : undefined;
      const equation = draft?.equation ?? '';
      const branches = draft?.branches ?? [makeConditionalBranch(feedback, 'migrate')];
      setConditionalEquation(equation);
      setConditionalBranches(branches);
      setActiveBranchIndex(0);
      savedConditionalDraftRef.current = undefined;
      push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, branches, equation);
    } else {
      // Banked under its own key and out of `conditionalBranches` at once. Recovery from an
      // accidental untick needs the set to survive, but the canvas card grades off the saved block,
      // so leaving it in place would have the card resolving a branch the editor says is off. A
      // separate key gives both, and unlike a memory-only draft it survives a reload.
      const draft = conditionalBranches !== undefined
        ? { equation: conditionalEquation ?? '', branches: conditionalBranches }
        : savedConditionalDraftRef.current;
      savedConditionalDraftRef.current = draft;
      bankedThisSessionRef.current = draft !== undefined;
      setConditionalEquation(undefined);
      setConditionalBranches(undefined);
      pushBranchState(undefined, undefined, draft);
    }
  }

  // A variable's presence in the question is what makes branch-based correctness meaningful at
  // all — showConditionalUI (in the editingSlot JSX) hides the whole toggle+equation+branch UI
  // once the last variable chip is removed, but hiding it isn't enough on its own: the branches
  // would still sit there, still consulted by evaluateFitb. Runs every render (no dependency
  // array) rather than trying to track `questionHtml` — mirrors Dropdown/Keyword's identical
  // effect for the full reasoning.
  useEffect(() => {
    if (!isEditing) return;
    const hasVariableNow = htmlHasVariableChips(questionHtml);
    if (!hasVariableNow && conditionalBranches !== undefined) toggleConditionalCorrectness(false);
  });

  function updateConditionalEquation(val: string) {
    setConditionalEquation(val);
    push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, conditionalBranches, val);
  }

  function addConditionalBranch() {
    const branches = conditionalBranches ?? [];
    const seedFrom = branches.length > 0 ? branches[branches.length - 1]!.feedback : feedback;
    const next = [...branches, makeConditionalBranch(seedFrom, 'fork')];
    setConditionalBranches(next);
    setActiveBranchIndex(next.length - 1);
    push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  function removeConditionalBranch(id: string) {
    const next = (conditionalBranches ?? []).filter(b => b.id !== id);
    setConditionalBranches(next);
    setActiveBranchIndex(i => Math.max(0, Math.min(i, next.length - 1)));
    push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, next);
    branchPrevButtonRef.current?.focus();
  }

  function updateConditionalBranch(id: string, patch: Partial<Pick<ConditionalRoleBranch, 'operator' | 'threshold' | 'thresholdHigh'>>) {
    const next = (conditionalBranches ?? []).map(b => b.id === id ? { ...b, ...patch } : b);
    setConditionalBranches(next);
    push(questionHtml, blanks, caseSensitive, accentSensitive, gradingMode, feedback, next);
  }

  function handleDone() {
    push();
    // Closing is what makes a switched-off branch set unrecoverable: the retained draft is
    // dropped from the saved block and undo becomes the only way back.
    if (savedConditionalDraftRef.current !== undefined && conditionalBranches === undefined) pushBranchState(undefined, undefined, undefined);
    savedConditionalDraftRef.current = undefined;
    if (embedded) { onClose?.(); return; }
    shouldFocusCollapsed.current = true;
    setIsEditing(false);
    onClose?.();
  }

  // ── Feedback branch scroller ──────────────────────────────────────────────────
  // Same shared activeBranchIndex the setup matrix above uses, in a separate control here so
  // switching branches can never accidentally leave the two views out of sync.
  // Graded-only, like every other branching surface (activeBranch, showConditionalUI): branches are
  // read straight from persisted data, so a block authored with branches and later switched to
  // Participation/Optional kept showing this scroller above states those branches can never reach.
  const feedbackBranchScroller = (
    /* Six blocks had written this identically; see `FeedbackBranchPager`. */
    <FeedbackBranchPager
      gradingMode={gradingMode}
      branches={conditionalBranches}
      activeIndex={activeBranchIndex}
      onActiveIndexChange={setActiveBranchIndex}
    />
  );

  // ── Grading mode metadata ─────────────────────────────────────────────────

  const MODES = gradingModes(t);
  const currentMode = MODES.find(m => m.id === gradingMode) ?? MODES[0]!;
  const nextMode = MODES[(MODES.indexOf(currentMode) + 1) % MODES.length]!;

  // ── Preview ───────────────────────────────────────────────────────────────

  if (isPreview) {
    // The version THIS student drew, from the task roll — see `versionRollKey`.
    const d = buildNormalizedFitbData(initData(block.data), t, taskPreviewRoll?.[versionRollKey(block.id)]);
    const dIds = getBlankIdsInOrder(d.question);
    const testData = memoizedByKey(
      /* The DRAWN VERSION is part of the key, or this cache answers for whichever version it saw
         first and the content never moves. */
      fitbTestDataCache, block.data,
      [variables, t, rolledValues, submittedValues, hasSubmitted, taskPreviewRoll?.[versionRollKey(block.id)],
       /* The LEADER's draw belongs in the key: the scenario decides the answer key. */
       drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)],
      // The ONE task roll both blocks read, so the leader's prose and this block's answer key
      // describe the same scenario (§3.1).
      () => computeFitbTestData(d, dIds, variables, t, rolledValues, submittedValues, hasSubmitted,
        drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)),
    );
    /* The same derivation asked about the DRAFT, `hasSubmitted` forced true so the chips light
       as though it had been handed in. Reported, never rendered. */
    const draftData = memoizedByKey(
      fitbDraftChipsCache, block.data,
      [variables, t, rolledValues, testValues, taskPreviewRoll?.[versionRollKey(block.id)],
       drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)],
      () => computeFitbTestData(d, dIds, variables, t, rolledValues, testValues, true,
        drawnScenarioId(initData(block.data).followsBlockId, taskPreviewRoll)),
    );
    return (
      <FitbAnswerDisplay
          scenario={scenario.ghostedScenario}
        question={testData.resolvedQuestion}
        values={testValues}
        onChange={handleTestChange}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      >
        {/* See keyword-block: only the block knows which conditions are in force. */}
        <ReportTestChips chips={draftData.testChips} states={draftData.testResult} />
        {renderFitbFiredFeedback(testData.testResult, rolledValues)}
      </FitbAnswerDisplay>
    );
  }

  // ── Collapsed ─────────────────────────────────────────────────────────────

  if (!isEditing) {
    // Through the version in force, so the card tests what the pager is pointed at.
    const d = buildNormalizedFitbData(initData(block.data), t, shownVersionId);
    const dIds = getBlankIdsInOrder(d.question);
    const displayMode = MODES.find(m => m.id === d.gradingMode) ?? MODES[0]!;
    const dispChipClass = d.gradingMode === 'graded' ? 'bg-ws-primary/10 text-ws-primary' : 'bg-ws-muted/10 text-ws-muted';
    // Blank-chip spans store their OWN id as fallback inner content (see blank-chip-extension.tsx's
    // renderHTML) — stripping tags off the raw question HTML directly would leak that raw id
    // straight into the accessible name (confirmed live: "Fill in the Blank block:
    // c84e92eb-b732-..."). Resolving blanks to their answer text first (same rendering already
    // used for the visual collapsed-card preview, FitbQuestionPreview's revealAnswers=true) fixes
    // this and keeps the label consistent with what's shown on screen.
    const fitbAriaLabel = d.question
      ? t('blocks.fitb.editAriaBaseWithQuestion', { question: stripHtmlForLabel(renderBlanksWithAnswers(d.question, d.blanks, t)).slice(0, 80) })
      : t('blocks.fitb.editAriaBase');

    // Every currently-active issue for this block, computed once — read by this bar (top-priority
    // one only) and, once the test panel lands, its diagnostics list too, so the surfaces can never
    // disagree about wording (see fitbIssues' own doc comment).
    const issues = settleStored(memoizedByKey(fitbIssuesCache, block.data, [variables, t, shownVersionId], () => fitbIssues(d, { ...issueCtx, blankIdsInOrder: dIds })));
    const issueBar = collapsedIssueBar(issues);
    const barNode = (() => {
      if (issueBar) return issueBar;
      if (isMain) return null;
      return collapsedModeBar(d.gradingMode, t, 'fitb');
    })();

    const testData = isSelected ? memoizedByKey(
      // Keyed on the shown version too — see the preview branch's note.
      fitbTestDataCache, block.data,
      [variables, t, rolledValues, submittedValues, hasSubmitted, shownVersionId, scenario.shownScenarioId],
      /* On the CARD the scenario is whichever one the pager is on, never a draw. */
      () => computeFitbTestData(d, dIds, variables, t, rolledValues, submittedValues, hasSubmitted,
        scenario.activeScenario?.scenarioId),
    ) : null;

    return (
      <CollapsedInputBlock
        block={block}
        blockRef={blockRef}
        contentRef={collapsedContentRef}
        icon={<FormInput className='h-5 w-5 text-ws-primary dark:text-ws-text-link' strokeWidth={2} />}
        titleLabel={t('blocks.fitb.title')}
        isMain={isMain}
        gradingMode={d.gradingMode}
        gradingModeLabel={displayMode.label}
        GradingModeIcon={displayMode.Icon}
        gradingModeChipClass={dispChipClass}
        isSelected={isSelected}
        isDragOver={isDragOver}
        isDeleteArmed={isDeleteArmed}
        onDisarmDelete={disarmDelete}
        onTriggerDuplicate={triggerDuplicate}
        onTriggerDelete={triggerDelete}
        a11yLabel={fitbAriaLabel}
        answerBarSlot={() => barNode}
        testEvaluation={testData?.testResult ? (testData.testResult[0]?.evaluation ?? 'neutral') : undefined}
        testBar={isSelected && testData ? {
          continuous,
          onContinuousChange: handleContinuousChange,
          onSubmit: handleTestSubmit,
          hasResult: hasSubmitted,
          chips: testData.testChips,
          onReset: handleTestReset,
          // Versions are rollable too: a banked block can be rerolled with no chips in it.
          onReroll: (testData.hasChips && testData.hasRollable) ? handleTestReroll : undefined,
          // Which branch resolved for this test, with a one-click jump straight to editing it —
          // deliberately NOT automatic on every reroll (see pendingBranchJump's doc comment).
          /* One row for everything this test resolved to — shared with the phone bar and every
             other banked block. See `version-branch-notice.tsx`. */
          topNotices: (
            <VersionBranchNotice
              versions={scenario.testPager?.versions ?? shownVersions}
              activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
              onSelectVersion={scenario.testPager?.onSelect ?? setTestVersion}
              noun={scenario.testPager?.noun ?? 'version'}
              editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
              stacked={isPhone}
              branch={testData.matchedBranch && testData.matchedBranchIndex !== -1 ? {
                index: testData.matchedBranchIndex,
                total: d.conditionalBranches?.length ?? 0,
                equation: d.conditionalEquation ?? '',
                rolledValues,
                value: testData.matchedEquationValue,
                operator: testData.matchedBranch.operator,
                threshold: testData.matchedBranch.threshold,
                thresholdHigh: testData.matchedBranch.thresholdHigh,
              } : null}
              onEdit={() => {
                if (testData.matchedBranch && testData.matchedBranchIndex !== -1) requestBranchJump(block.id, testData.matchedBranchIndex);
                editShownVersion(testData.matchedBranch?.id);
                if (onSelect) onSelect();
                else { shouldFocusEditor.current = true; setIsEditing(true); }
              }}
            />
          ),
          notices: (
            <IssuesPanel issues={issues} phase='settled' className='rounded-md border border-ws-border px-3 py-2' />
          ),
        } : undefined}
        onActivate={() => {
          if (onSelect) { onSelect(); }
          else { shouldFocusEditor.current = true; setIsEditing(true); }
        }}
      >
        {isSelected && testData ? (
          <FitbAnswerDisplay
          scenario={scenario.ghostedScenario}
            question={testData.resolvedQuestion}
            values={testValues}
            onChange={handleTestChange}
            activeEditorRef={activeEditorRef}
            setActiveEditor={setActiveEditor}
          >
            {renderFitbFiredFeedback(testData.testResult, rolledValues)}
          </FitbAnswerDisplay>
        ) : (
          <BlockQuestion
            html={d.question} spaced showEmptyNote
            renderer={({ html, className }) => (
              <FitbQuestionPreview html={html} blanks={d.blanks} revealAnswers className={className} />
            )}
          />
        )}
      </CollapsedInputBlock>
    );
  }

  // ── Feedback slots ──────────────────────────────────────────────────────────

  const unansweredState = activeFeedbackStates.find(s => s.trigger === 'unanswered');
  const noAnswerSlot = unansweredState ? (() => {
    const s = unansweredState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.noAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => {
          const cur = s.evaluation === 'correct' ? 'neutral' : s.evaluation as 'neutral' | 'incorrect';
          updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(cur) + 1) % ORDER.length]! });
        }}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : null;

  // One derivation for every reachability question the panel asks below.
  const panelReach = fitbReachability({ gradingMode, feedback: { states: activeFeedbackStates } }, effectiveBlanksForActive);
  const panelCanFire = (trigger: string) => panelReach.states.find(x => x.trigger === trigger)?.reachable ?? false;

  const partialAnswerState = activeFeedbackStates.find(s => s.trigger === 'incomplete_answer');
  const partialAnswerSlot = panelCanFire('incomplete_answer') && partialAnswerState ? (() => {
    const s = partialAnswerState;
    const ORDER = ['neutral', 'incorrect'] as const;
    return (
      <FeedbackCard fieldId={s.id}
        title={getTitle(s)} content={s.content} evaluation={s.evaluation}
        locked evalLocked={false} lockedTriggerLabel={t('blocks.feedbackDefaults.incompleteAnswerTitle')}
        onTitleChange={() => {}} onDelete={() => {}}
        onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
        onEvaluationCycle={() => updateActiveFeedbackState(s.id, { evaluation: ORDER[(ORDER.indexOf(s.evaluation as 'neutral' | 'incorrect') + 1) % ORDER.length]! })}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    );
  })() : undefined;

  // Mode-scoped: optional is INERT and shows no targeted state at all, and `set_answer` is
  // hidden in graded where Correct already says it. Hidden, never deleted — see `targetedFiresIn`.
  const targetedStates = visibleTargeted(activeFeedbackStates, gradingMode);
  const visibleTargetedStates = targetedStates.filter(s => !isTargetedStaleForActive(s));

  const usedTriggers = new Set(targetedStates.map(s => s.trigger));
  // No mode gate: `effectiveBlanksForActive` is already through `rolesForMode`, so outside graded
  // every word reads neutral and so every word is targetable. The gate used to be the only reason
  // FITB could never author a target in survey or optional.
  const canAddMore = effectiveBlanksForActive.some(b => b.words.some(w => w.role !== 'correct' && !usedTriggers.has(buildFitbWordTrigger(b.id, w.id))))
    || (gradingMode === 'optional' && effectiveBlanksForActive.some(b => !usedTriggers.has(buildFitbWordTrigger(b.id, FITB_BLANK_EMPTY))));

  const targetedSlot = (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleFeedbackTargetedDragEnd}
      accessibility={dndA11y(t, visibleTargetedStates.map(x => x.id), 'feedback')}
    >
      <SortableContext items={visibleTargetedStates.map(s => s.id)} strategy={verticalListSortingStrategy}>
        <div className='flex flex-col gap-3'>
          {visibleTargetedStates.map(s => {
            const usedByOthers = new Set(activeFeedbackStates.filter(st => st.type === 'targeted' && st.id !== s.id).map(st => st.trigger));
            const triggerOptions: TriggerOption[] = effectiveBlanksForActive.flatMap((blank, blankIdx) => [
              /**
               * "Blank N: No Response" — per BLANK, because that is the only level at which the
               * question has one answer. Always offered while it is this card's own trigger, or
               * the card would lose its label the moment it was authored.
               */
              ...(gradingMode === 'optional'
                && (s.trigger === buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY)
                    || !usedByOthers.has(buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY)))
                ? [{
                    value: buildFitbWordTrigger(blank.id, FITB_BLANK_EMPTY),
                    label: t('blocks.fitb.triggerOptionLabel', {
                      ordinal: blankIdx + 1,
                      word: t('blocks.feedbackDefaults.noResponseTitle'),
                    }),
                    evaluation: 'neutral' as TriggerOption['evaluation'],
                  }]
                : []),
              ...blank.words.flatMap((word, wordIdx) => {
                const trigger = buildFitbWordTrigger(blank.id, word.id);
                if (word.role === 'correct' || (usedByOthers.has(trigger) && trigger !== s.trigger)) return [];
                return [{
                  value: trigger,
                  label: t('blocks.fitb.triggerOptionLabel', {
                    ordinal: blankIdx + 1,
                    word: word.text || t('blocks.fitb.wordLabel', { number: wordIdx + 1 }),
                  }),
                  evaluation: (word.role === 'incorrect' ? 'incorrect' : 'neutral') as TriggerOption['evaluation'],
                }];
              }),
            ]);
            const currentOpt = triggerOptions.find(o => o.value === s.trigger) ?? triggerOptions[0];
            return (
              <SortableFeedbackCard
                key={s.id}
                sortId={s.id}
                title={s.title} content={s.content} evaluation={displayEvaluation(gradingMode, s.evaluation)}
 /* Collapse on a grading-mode switch: these cards do not remount, so one the teacher
    had collapsed sprang back open every time they flipped modes to compare. */
 collapseSignal={gradingMode}
                startExpanded={justAddedKey(s.id) === justAddedStateId}
                titleLocked
                evalLocked
                triggerZone={<TriggerDropdown value={s.trigger} options={triggerOptions} onChange={trigger => {
                  const parsed = parseFitbWordTrigger(trigger);
                  const blankIdx = parsed ? effectiveBlanksForActive.findIndex(b => b.id === parsed.blankId) : -1;
                  const word = blankIdx === -1 ? undefined : effectiveBlanksForActive[blankIdx]!.words.find(w => w.id === parsed!.wordId);
                  const wordIdx = blankIdx === -1 ? -1 : effectiveBlanksForActive[blankIdx]!.words.findIndex(w => w.id === parsed!.wordId);
                  const title = blankIdx !== -1 && wordIdx !== -1
                    ? fitbTargetedTitle(blankIdx, effectiveBlanksForActive[blankIdx]!.words[wordIdx]!, wordIdx, t)
                    : s.title;
                  updateActiveFeedbackState(s.id, {
                    trigger,
                    title,
                    evaluation: word ? (word.role === 'neutral' ? 'neutral' : 'incorrect') : s.evaluation,
                  });
                }} />}
                triggerZoneSummary={currentOpt ? <FitbTriggerChip option={currentOpt} /> : undefined}
                onTitleChange={() => {}}
                onContentChange={html => updateActiveFeedbackState(s.id, { content: html })}
                onEvaluationCycle={() => {}}
                onDelete={() => deleteActiveFeedbackState(s.id)}
                activeEditorRef={activeEditorRef}
                setActiveEditor={setActiveEditor}
              />
            );
          })}
        </div>
      </SortableContext>
    </DndContext>
  );

  // Both gates come from the derivation, over the branch-resolved blanks the panel is editing.
  // It subsumes what `fitbBranchMissingBlanks` and `isSetupIncomplete` answered separately here,
  // and is stricter than the former in one way that matters: a correct-roled word with no text is
  // not an answer, so a branch naming one is still missing that blank.
  //
  // Unlike Correct, Default Incorrect never goes inactive just because one blank lacks a correct
  // word — that broken blank makes `incorrect` the *guaranteed* terminal result whenever blanks
  // are filled, not an unreachable one. It only goes inactive when there's nothing to submit at
  // all (mirrors Keyword's own "incorrectSlot never goes inactive" free-text precedent).
  const setupIncompleteForFeedback = gradingMode === 'graded' && !panelCanFire('correct');
  const noBlanksAtAll = !panelCanFire('incorrect');

  // Per-blank correct/incorrect word combos, mirroring Keyword's defaultCorrectInline/
  // defaultIncorrectInline chip lists exactly — just grouped one row per blank, since each blank
  // is independently graded rather than being one shared answer field. Built from
  // effectiveBlanksForActive so these summaries reflect whichever branch is currently being edited.

  // One wrapping row with the connectors written in, not one silent row per blank. The stacked rows
  // left the relationship between blanks to the layout, so the panel never said whether a second
  // blank was an additional REQUIREMENT or an alternative — while the legend, drawing the same
  // condition, wrote "and"/"or" between them the whole time.
  const chipRow = (chips: ConditionChips) => (
    <ConditionChipRow chips={chips} unreachableLabel={t('blocks.feedbackPanel.unreachable')} />
  );
  const defaultCorrectInline = chipRow(fitbChips('correct', panelReach.correctByBlank, t));
  const defaultIncorrectInline = chipRow(fitbChips('incorrect', panelReach.uncoveredByBlank, t));

  /**
   * The catch-all names what it still catches, per blank, exactly as Default Incorrect does — it
   * used to be a fixed tick and a mode label, which said nothing about which words were already
   * spoken for. `catchAllChips` keeps the plain label for a block with no targets authored.
   */
  const participationCatchAllInline = chipRow(catchAllChips(gradingMode, visibleTargetedStates.length > 0, t,
    () => fitbChips('incorrect', panelReach.uncoveredByBlank, t, gradingMode, new Set(targetedStates.map(x => x.trigger))),
    ));
  const positiveTriggerZone =
    gradingMode === 'graded' ? defaultCorrectInline : participationCatchAllInline;

  const correctSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && (s.trigger === 'correct' || s.trigger === 'any_answer' || s.trigger === 'any_response'))
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={gradingMode === 'graded' && setupIncompleteForFeedback}
        triggerZone={positiveTriggerZone}
        triggerZoneSummary={positiveTriggerZone}
        title={catchAllTitle(gradingMode, visibleTargetedStates.length > 0, t) ?? getTitle(state)}
        content={state.content}
        /**
         * The tick goes the moment a target exists — the catch-all must not affirm an answer the
         * teacher ignored more strongly than one they wrote for. With no targets the block is
         * still a plain questionnaire and keeps it. Same rule `catchAllChips` applies to the chip
         * beside it, so the card and its trigger cannot disagree.
         */
        evaluation={gradingMode !== 'graded' && visibleTargetedStates.length > 0 ? 'neutral' : state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  const incorrectSlot = activeFeedbackStates
    .filter(s => s.type === 'default' && s.trigger === 'incorrect')
    .map(state => (
      <FeedbackCard fieldId={state.id}
        key={state.id}
        locked inactive={noBlanksAtAll}
        triggerZone={defaultIncorrectInline}
        triggerZoneSummary={defaultIncorrectInline}
        title={getTitle(state)}
        content={state.content}
        evaluation={state.evaluation}
        onTitleChange={() => {}}
        onContentChange={(html) => updateActiveFeedbackState(state.id, { content: html })}
        onEvaluationCycle={() => {}}
        onDelete={() => {}}
        activeEditorRef={activeEditorRef}
        setActiveEditor={setActiveEditor}
      />
    ));

  // ── Phone Test tab ────────────────────────────────────────────────────────
  // Same InputBlockFrame testSlot/testBar/phoneMode wiring as MCQ/Dropdown/Keyword, fed from
  // `liveD` instead of `initData(block.data)` so the test tab reflects the editor's own live state.
  //
  // Gated on `isPhone` (not `phoneTestMode === 'test'`) — matching MCQ/Keyword/Dropdown's own
  // identical phoneTest, all three of which compute this whenever `isPhone`, regardless of which
  // tab is currently showing. Confirmed live this was a real bug: InputBlockFrame only renders its
  // Edit/Test toggle once testSlot AND testBar are both truthy (hasPhoneTestToggle), so gating them
  // on 'test' mode already being active made the toggle itself invisible in Edit mode — there was
  // no way to ever reach Test mode in the first place.
  const phoneTest = isPhone ? computeFitbTestData(liveD, idsInOrder, variables, t, rolledValues, submittedValues, hasSubmitted) : null;
  const phoneTestSlot = phoneTest ? (
    <FitbAnswerDisplay
          scenario={scenario.ghostedScenario}
      question={phoneTest.resolvedQuestion}
      values={testValues}
      onChange={handleTestChange}
      activeEditorRef={activeEditorRef}
      setActiveEditor={setActiveEditor}
    >
      {renderFitbFiredFeedback(phoneTest.testResult, rolledValues)}
    </FitbAnswerDisplay>
  ) : null;
  const phoneTestBar: TestBarProps | undefined = phoneTest ? {
    continuous,
    onContinuousChange: handleContinuousChange,
    onSubmit: handleTestSubmit,
    hasResult: hasSubmitted,
    chips: phoneTest.testChips,
    onReset: handleTestReset,
    onReroll: (phoneTest.hasChips && phoneTest.hasRollable) ? handleTestReroll : undefined,
    /* The same row as the canvas card's — one component, so the two can never drift. */
    topNotices: (
      <VersionBranchNotice
        versions={scenario.testPager?.versions ?? shownVersions}
        activeVersionId={scenario.testPager ? scenario.testPager.activeId : shownVersionId}
        /* The EDITOR's pointer here: on a phone, Edit and Test are two tabs over ONE session. */
        onSelectVersion={scenario.testPager?.onSelect ?? selectVersion}
        noun={scenario.testPager?.noun ?? 'version'}
        editScrollsTo={scenario.activeScenario?.scenarioId ? { fieldId: scenario.activeScenario?.scenarioId, blockId: block.id } : undefined}
        stacked
        branch={phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1 ? {
          index: phoneTest.matchedBranchIndex,
          total: conditionalBranches?.length ?? 0,
          equation: conditionalEquation ?? '',
          rolledValues,
          value: phoneTest.matchedEquationValue,
          operator: phoneTest.matchedBranch.operator,
          threshold: phoneTest.matchedBranch.threshold,
          thresholdHigh: phoneTest.matchedBranch.thresholdHigh,
        } : null}
        onEdit={() => {
          if (phoneTest.matchedBranch && phoneTest.matchedBranchIndex !== -1) {
            setActiveBranchIndex(phoneTest.matchedBranchIndex);
          }
          editShownVersion(phoneTest.matchedBranch?.id);
          setPhoneTestMode('edit');
        }}
      />
    ),
    notices: (
      <IssuesPanel issues={editorIssues} phase='editing' className='rounded-md border border-ws-border px-3 py-2' />
    ),
  } : undefined;

  // ── Editing shell ───────────────────────────────────────────────────────────

  return (
    <InputBlockFrame
      {...angleModeProps}
      blockId={block.id}
      icon={<FormInput className='h-6 w-6' strokeWidth={2} />}
      titleLabel={t('blocks.fitb.title')}
      blockName={block.name ?? ''}
      onBlockNameChange={onRename}
      isBlockNameTaken={(candidate) => isNameTaken(candidate, blocks ?? [], block.id)}
      closeA11yLabel={t('blocks.fitb.closeEditor')}
      onClose={handleDone}
      fullScreen={isPhone && !embedded}
      landscapeLayout={isPhoneLandscape}
      gradingMode={gradingMode}
      gradingModeLabel={currentMode.label}
      GradingModeIcon={currentMode.Icon}
      gradingModeCycleA11y={t('blocks.gradingModes.cycleLabel', { current: currentMode.label, next: nextMode.label })}
      onGradingModeCycle={() => handleGradingMode(nextMode.id)}
      testSlot={phoneTestSlot}
      testBar={phoneTestBar}
      editModeLabel={t('blocks.phoneTabs.edit')}
      testModeLabel={t('blocks.phoneTabs.test')}
      phoneMode={phoneTestMode}
      onPhoneModeChange={setPhoneTestMode}
      warningBarSlot={editorIssues.length > 0 ? (
        <IssuesPanel issues={editorIssues} phase='editing' className='px-4 py-2.5' />
      ) : undefined}
      settingsSlot={
        <>
          <OptionsSection title={t('blocks.optionsSections.answerFormat')}>
            <CheckboxRow fieldId='caseSensitive'
              // Every option this block has is a MATCHING rule, and optional matches nothing —
              // it accepts whatever arrives. Disabled in place: a lone control, not a section.
              disabled={!settingAvailable('caseSensitive', gradingMode)}
              checked={caseSensitive}
              onToggle={() => handleCaseSensitive(!caseSensitive)}
              label={t('blocks.matching.caseSensitive')}
            />
            <CheckboxRow fieldId='accentSensitive'
              // Every option this block has is a MATCHING rule, and optional matches nothing —
              // it accepts whatever arrives. Disabled in place: a lone control, not a section.
              disabled={!settingAvailable('accentSensitive', gradingMode)}
              checked={accentSensitive}
              onToggle={() => handleAccentSensitive(!accentSensitive)}
              label={t('blocks.matching.accentSensitive')}
            />
          </OptionsSection>
        </>
      }
      settingsA11yLabel={t('blocks.fitb.optionsDrawerToggle')}
      settingsLabel={t('common.options')}
      feedbackLabel={t('blocks.areas.feedback')}
      defaultIsEditingFeedback={initialIsEditingFeedback}
      onFeedbackModeChange={onFeedbackModeChange}
      sharedToolbar={sharedToolbar}
      feedbackPanelProps={{
        beforeSlot: scenario.feedbackScroller ?? feedbackBranchScroller,
        noAnswerSlot,
        partialAnswerSlot,
        correctSlot,
        targetedSections: [{
          key: 'targeted',
          heading: t('blocks.feedbackPanel.targeted'),
          content: targetedSlot,
          canAddMore,
          addLabel: t('blocks.feedbackPanel.add'),
          onAdd: addTargetedState,
          isEmpty: visibleTargetedStates.length === 0,
        }],
        incorrectHeading: t('blocks.feedbackPanel.defaultIncorrect'),
        incorrectSlot,
      }}
      editingSlot={
          <div className='flex flex-col gap-4 px-6 py-4'>
          {/* A bank is a niche feature: in the heading it is available without being advertised. */}
          {/* The three-way offer, when linking meets an existing bank — §7.1. */}
          {scenario.remapModal}
          {/* The link control and what it points at, for BOTH kinds of link — see
              `VariesWithSection`, which decides on its own whether a DRIVEN block
              needs it. This was five lines of identical JSX in twelve blocks. */}
          <VariesWithSection
            following={!!scenario.activeScenario}
            linkMenu={scenario.linkMenu}
            stimulus={scenario.editorScenario}
          />
          {/* EVERYTHING THE ROW OWNS, inside the row's own box — the question as well as
              the answer setup, because a driven axis varies the question too. Renders its
              children bare when this block is not driven. See `DrivenRowFrame`. */}
          <DrivenRowFrame block={block} data={initData(block.data)} emit={emit}>
          <EditorSectionHeading
            label={t('blocks.areas.question')}
            /* First heading in the panel unless a scenario is above it. */
            divider={!!scenario.activeScenario}
            action={(
              <div className='flex items-center gap-2'>
              {/* The bank is GONE while this block follows a scenario, not merely inert (§2.2):
                  versions are many stems each with one answer setup, a scenario is one stem
                  with many, and the two cannot both be the answer key. */}
              {!scenario.following && (
              <VersionSelector
                versions={(initData(block.data).versions ?? []) as { id: string }[]}
                activeId={editorVersionId}
                onSelect={selectVersion}
                onAdd={() => {
                  const next = addFitbVersion(initData(block.data), editorVersion, () => crypto.randomUUID());
                  emit(versionStructure(block.data, next));
                  const added = next.versions?.[next.versions.length - 1];
                  if (added) selectVersion(added.id, next);
                }}
                onRemove={(id) => {
                  const next = removeFitbVersion(initData(block.data), id);
                  emit(versionStructure(block.data, next));
                  const landing = next.versions?.[0];
                  selectVersion(landing?.id, next);
                  setTestVersion(landing?.id);
                }}
              />
              )}
              {/* Silent once there is a Varies-With section, which is where the
                  control moves to — see `QuestionHeadingLink`. Written as
                  `!activeScenario` it knew about scenarios only, so a DRIVEN block
                  showed the link twice and the heading overflowed sideways. */}
              <QuestionHeadingLink
                following={!!scenario.activeScenario}
                linkMenu={scenario.linkMenu}
              />
              </div>
            )}
          />
          <BlockQuestionEditor
            value={questionHtml}
            onChange={handleQuestionChange}
            toolbar={sharedToolbar}
            editorRef={questionEditorRef}
            shouldFocusRef={shouldFocusEditor}
            embedded={embedded}
            placeholder={t('blocks.fitb.questionPlaceholder')}
          />

          <AddRowButton
            label={t('blocks.fitb.insertBlank')}
            onClick={insertBlank}
          />

          {/* Outside the gradingMode ternary below: the boundary between authoring the passage and
              defining correctness exists in survey and optional mode too, where what follows is the
              "any word accepted" note rather than a blank list. */}
          {/*
            * ANSWER SETUP, hidden in OPTIONAL only.
            *
            * Optional is the one mode with no use for an answer key — it accepts whatever arrives
            * and records it. Survey keeps the list: a targeted state can speak to a particular
            * word there, so the words are live even though nothing is graded.
            *
            * Hidden rather than greyed, because the whole SECTION's job is gone — the same call
            * the feedback panel already makes for its correctness cards. Nothing is deleted:
            * switch back and every word is as it was.
            */}
          {gradingMode !== 'optional' && (<>
            <EditorSectionHeading label={t('blocks.areas.answerSetup')} />

            {/* No mode gate — see Keyword's twin. The branch UI inside is graded-only on its own
                terms; the ternary additionally hid the WORD LISTS behind "Any word will be
                accepted", which stopped being true once a word could carry targeted feedback. */}
            {(() => {
              // A variable's presence in the question is what makes branch-based correctness
              // meaningful at all — showConditionalUI hides the whole toggle+equation+matrix UI
              // once the last variable chip is removed (a separate effect clears the branch data
              // itself in that case, see its own doc comment above).
              const hasVariable = htmlHasVariableChips(questionHtml);
              /* FOLLOWING NO LONGER RULES BRANCHING OUT — when both are on the branching nests
                 INSIDE the scenario row. See `use-nested-branching`. */
              const showConditionalUI = hasVariable && gradingMode === 'graded';
              const isEnabled = conditionalBranches !== undefined;
              /* WHOSE branch set is on screen — the row's when following, the block's otherwise. */
              const { nesting, effEquation, effEnabled, effBranches, effIndex, effActiveBranch } =
                nestedBranching<FitbBlockData['feedback']>({
                  activeScenario: scenario.activeScenario,
                  blockEquation: conditionalEquation,
                  blockBranches: conditionalBranches ?? [],
                  blockEnabled: isEnabled,
                  activeBranchIndex,
                  graded: gradingMode === 'graded',
                });
              const makeRow = (mode: SeedMode) => makeRowBranch<FitbBlockData['feedback']>({
                mode,
                roles: (scenario.activeScenario as { roles?: Record<string, unknown> } | undefined)?.roles ?? {},
                feedback: (scenario.activeScenario as unknown as { feedback: FitbBlockData['feedback'] }).feedback,
                cloneFeedback: cloneFeedbackWithFreshIds,
                seedFeedback: seedBranchFeedback,
              });
              /** One toggle, two scopes — the ROW's while following, the block's otherwise. */
              const toggleRowBranching = () => {
                if (!nesting) { toggleConditionalCorrectness(!isEnabled); return; }
                setActiveBranchIndex(0);
                scenario.writeActiveRow((effEnabled
                  ? { conditionalEquation: undefined, conditionalBranches: undefined }
                  : { conditionalEquation: effEquation ?? '',
                      conditionalBranches: [makeRow('migrate')] }) as never);
              };
              /*
               * WHERE THE TOGGLE SITS IS A CLAIM ABOUT WHAT IT APPLIES TO. On a follower it turns
               * branching on for ONE ROW, so standing above the scenario box reads as "every scenario".
               */
              const branchingToggle = showConditionalUI ? (
                <BranchingCorrectsRow
                  checked={effEnabled}
                  onToggle={toggleRowBranching}
                />
              ) : null;
              const blockLevelToggle = showConditionalUI && !nesting;
              const branches = conditionalBranches ?? [];
              const clampedIndex = Math.min(activeBranchIndex, Math.max(0, branches.length - 1));
              const activeBranchLocal = isEnabled && gradingMode === 'graded' ? branches[clampedIndex] : undefined;
              const hasEquation = !!conditionalEquation?.trim();
              // minSampleValues, NOT `rolledValues` — that state stays {} until the block is
              // actually isSelected, which would make every equation referencing a variable look
              // invalid the entire time it's being authored.
              const equationInvalid = hasEquation && evaluateEquation(conditionalEquation!, minSampleValues(variables), angleMode) === null;
              const equationWorks = hasEquation && !equationInvalid;

              // Same blank/word list either way — a branch only ever remaps `role`, never touches
              // text/identity — so both the static and branch-scoped views share this one JSX
              // builder, just fed a different blanks array and role-click handler. Each blank keeps
              // its own "Add Word" button (branching or not) since a word's "add" action is
              // inherently scoped to one specific blank, unlike Keyword/Dropdown's single flat
              // option list.
              const activeRow = scenario.activeScenario ?? activeBranchLocal;
              const blanksToRender = activeRow ? effectiveBlanksForActive : visibleBlanks;
              const blanksListJsx = blanksToRender.length > 0 && (
                <div className='flex flex-col gap-4'>
                  {blanksToRender.map((blank, idx) => (
                    <div key={blank.id} data-field-id={blank.id} role='group' aria-label={t('blocks.fitb.blankLabel', { ordinal: idx + 1 })} className='flex flex-col gap-2'>
                      <p className='text-xs font-semibold text-ws-muted'>{t('blocks.fitb.blankLabel', { ordinal: idx + 1 })}</p>
                      <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={(e) => handleWordDragEnd(blank.id, e)}
                        accessibility={dndA11y(t, blank.words.map(x => x.id), 'word')}
      >
                        <SortableContext items={blank.words.map(w => w.id)} strategy={verticalListSortingStrategy}>
                          <div className='flex flex-col gap-2'>
                            {blank.words.map(word => (
                              <SortableWordRow
                                key={word.id}
                                word={{ ...word, role: roleForMode(gradingMode, word.role) }}
                                wordCount={blank.words.length}
                                roleLocked={gradingMode !== 'graded'}
                                armedWordKey={armedWordKey}
                                setArmedWordKey={setArmedWordKey}
                                armedKey={`${blank.id}:${word.id}`}
                                onRoleClick={scenario.activeScenario ? handleScenarioWordRoleClick
                                  : activeBranchLocal ? (wordId) => handleBranchWordRoleClick(activeBranchLocal.id, wordId)
                                    : (wordId) => handleWordRoleClick(blank.id, wordId)}
                                onTextChange={(wordId, text) => updateWordText(blank.id, wordId, text)}
                                onRemove={(wordId) => removeWord(blank.id, wordId)}
                                activeEditorRef={activeEditorRef}
                                setActiveEditor={setActiveEditor}
                                hasDuplicate={collidingWordIds.has(word.id)}
                              />
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                      <AddRowButton
                        label={t('blocks.fitb.addWord')}
                        onClick={() => addWord(blank.id)}
                      />
                    </div>
                  ))}
                </div>
              );

              return (
                <>
                  {blockLevelToggle && <div className='flex flex-col gap-2 pl-1'>{branchingToggle}</div>}

                  {showConditionalUI && isEnabled && activeBranchLocal ? (
                    <BranchConditionCarousel
                      seedVariable={(() => { const n = variableChipNames(questionHtml); return n.length === 1 ? n[0] : undefined; })()}
                      ref={branchPrevButtonRef}
                      equation={conditionalEquation ?? ''}
                      onEquationChange={updateConditionalEquation}
                      variables={variables}
                      rolledValues={rolledValues}
                      angleMode={angleMode}
                      equationInvalid={equationInvalid}
                      equationWorks={equationWorks}
                      hasError={editorIssues.some(i => i.severity === 'error' && ['no-equation', 'invalid-equation', 'coverage-gap'].includes(i.key))}
                      branches={branches}
                      activeIndex={clampedIndex}
                      onActiveIndexChange={setActiveBranchIndex}
                      onBranchChange={updateConditionalBranch}
                      onAdd={addConditionalBranch}
                      onRemove={removeConditionalBranch}
                      onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                    >
                      {/* More expansive than Keyword/Dropdown's own matrix wrapper — this shows
                          every blank's own word list at once (a real matrix, not one flat option
                          list), so it scrolls internally on its own, with the branch-nav row
                          staying visible above it by DOM order (not position:sticky — that row
                          sits outside/above this scroll container entirely), rather than growing
                          the whole editor panel unboundedly tall. */}
                      <div className='max-h-[28rem] overflow-y-auto pr-1'>
                        {blanksListJsx}
                      </div>
                    </BranchConditionCarousel>
                  ) : (
                    /* The scenario's own box — the carousel's shape with a variant in place of
                       an equation, and nothing at all when this block follows nothing. */
                    <ScenarioSetupFrame
                      scenario={scenario.activeScenario}
                      name={(scenario.activeScenarioVersion as { name?: string } | null)?.name}
                      pager={scenario.pager}
                      unreviewedCount={scenario.unreviewedCount}
                      onDismissUnreviewed={scenario.dismissUnreviewed}
                    >
                      {/* Only when there IS a box: the frame passes children through unwrapped when this
                          block follows nothing, so an ungated toggle renders a SECOND copy here. */}
                      {nesting && branchingToggle && <div className='pb-2'>{branchingToggle}</div>}
                      {showConditionalUI && effEnabled && effActiveBranch ? (
                        <BranchConditionCarousel
                          seedVariable={(() => { const n = variableChipNames(questionHtml); return n.length === 1 ? n[0] : undefined; })()}
                          equation={effEquation ?? ''}
                          onEquationChange={v => scenario.writeActiveRow({ conditionalEquation: v } as never)}
                          variables={variables}
                          rolledValues={rolledValues}
                          angleMode={angleMode}
                          equationInvalid={false}
                          equationWorks={!!effEquation?.trim()}
                          hasError={false}
                          branches={effBranches}
                          activeIndex={effIndex}
                          onActiveIndexChange={setActiveBranchIndex}
                          onBranchChange={(id, patch) => scenario.writeActiveRow({
                            conditionalBranches: effBranches.map(b => (b.id === id ? { ...b, ...patch } : b)) } as never)}
                          onAdd={() => scenario.writeActiveRow({
                            conditionalBranches: [...effBranches, makeRow('fork')] } as never)}
                          onRemove={id => scenario.writeActiveRow({
                            conditionalBranches: effBranches.filter(b => b.id !== id) } as never)}
                          onFieldFocus={() => { activeEditorRef.current = null; setActiveEditor(null); }}
                        >
                      {blanksToRender.length > 0 && (
                        <div className='flex flex-col gap-4'>
                          {blanksListJsx}
                        </div>
                      )}
                        </BranchConditionCarousel>
                      ) : (<>
                      {blanksToRender.length > 0 && (
                        <div className='flex flex-col gap-4'>
                          {blanksListJsx}
                        </div>
                      )}
                      </>)}
                    </ScenarioSetupFrame>
                  )}
                </>
              );
            })()}
          </>)}
          </DrivenRowFrame>
          </div>
      }
    />
  );
}
